import React from 'react';
import {
    FormGroup,
    Button,
    Badge,
    Spinner,
    UncontrolledPopover,
    PopoverHeader,
    PopoverBody,
    List,
    FormText,
    UncontrolledTooltip,
} from 'reactstrap';
import moment from 'moment-timezone';
import {
    isEqual, uniqBy, isEmpty,
} from 'lodash-es';
import cls from 'classnames';
import { faSave } from '@fortawesome/free-regular-svg-icons';
import {
    faUserClock,
    faCircleNotch,
    faTrash,
    faEraser,
    faPrint,
    faCircleCheck,
    faTriangleExclamation,
    faEye,
    faArrowUpRightFromSquare,
    faUndo,
    faCheck,
    faTimes,
    faSync,
    faExclamationTriangle,
    faPen,
    faFileImage,
    faUpload,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { Link } from '@s20/components/common/Link';
import { crewLeadLabel } from '@s20/i18n';
import {
    appendTimeZone, convertWinToIANA, formatOffset, formatOffsetInTimeZone, getTimeZoneAbbr,
} from '@s20/common/util/date';
import { getMapUrl } from '@s20/common/util/dom';
import { getCounty, getCountyName } from '@s20/common/util/location';
import { formatMinutes } from '@s20/common/util/number';
import { cleanText } from '@s20/common/util/string';
import { httpClient } from '@s20/services/httpClient';
import SlideForm from '../common/forms/SlideForm';
import SignatureImage from '../common/forms/SignatureImage';
import CommonContext, {
    CancellationNoteTypes, ApiRoutes, ApplicationPermissions, ChargeTypeResourceTypes,
    RentalUnitTypes, AppNavPaths, MomentDateFormat, SupportedFileExtensions, TimesheetAttachmentScope,
} from '../Common';
import {
    UncontrolledCollapseUnderlineHeader,
    FormLabel,
    onFieldChange,
    onReactSelectChanged,
    FlexStartRow,
    FlexCenterRow,
    toasty,
    FormBlocker,
    FormCheckbox,
    FlexRow,
    Input,
    Textarea,
    FlexBetweenRow,
    IconButton,
} from '../common/forms/FormElements'; // , FlexColumnStart } from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { TimesheetDetailsAdjustment } from '../timesheetManagement/TimesheetDetailsAdjustment';
import { Can } from '../Can';
import ValidatedSelect, {
    CompactSelectStyles,
} from '../common/forms/ValidatedSelect';
import { NumericInput } from '../common/forms/NumericInput';
import { ChargeTypeUnits, PayrollChargeTypes } from '../chargeType/ChargeType';
import { JobDetails } from '../job/Job';
import OrganizationContactForm from '../organization/OrganizationContactForm';
import { CountyCaptureType } from '../tenant/Tenant';
import { CountyLabel } from '../uscounties/CountyLabel';
import TimePicker from '../common/forms/TimePicker';
import { DatePicker } from '../common/forms/DatePicker';
import FileList from '../common/forms/FileList';
import FormErrorResponseDisplay from '../common/forms/FormErrorResponseDisplay';
import JotFormModal, { JotFormModalModes } from '../jotForm/JotFormModal';
import { DEFAULT_DATETIME_FORMAT } from '../common/util';
import FileUpload from '../common/forms/FileUpload';
import LoadingOverlay from '../common/LoadingOverlay';
import { Alert, ConfirmDelete, Modal } from '../common/Modal';
import AddressFormNew from '../address/AddressFormNew';
import { TimesheetStatus } from './Timesheet';
import { AuditPlaces } from './AuditPlaces';
import './TimesheetDetails.scss';
import { EquipmentLogModalBody } from './EquipmentLogModalBody';
import RejectTimesheetModalBody from './RejectTimesheetModalBody';
import TimesheetFlagsSection from './TimesheetFlagsSection';

const getExistingForemanOrRequestorIds = (details) => {
    if (!details) {
        return [];
    }

    return Array.from(
        new Set([
            details.requestorId,
            details.initialRequestorId,
            details.foremanId,
            details.initialForemanId,
        ]),
    );
};

const mapContacts = (customerContacts, existingForemanOrRequestorIds) => [
    ...(customerContacts ?? [])
        .filter(
            (c) => c.isActive
                || (existingForemanOrRequestorIds || []).includes(c.id),
        )
        .map((x) => ({ label: x.contactNameAndPhone, value: x.id })),
];

const formatAddress = (location) => {
    if (!location) return '';
    return `${location.address1}${location.address2 ? ' ' + location.address2 : ''} ${location.city}, ${location.state} ${location.zip}`;
};

const showWorkCardModal = (workCard) => {
    Alert.show({
        title: `Work Card - ${workCard.empName}`,
        size: 'xl',
        body: (
            <div
                className="overflow-auto text-center"
                style={{ maxHeight: '75dvh' }}
            >
                <img
                    src={workCard.workCardUrl}
                    alt="Work Card"
                    className="img-fluid"
                />
            </div>
        ),
    });
};

const onFileDownload = (file) => {
    if (file?.id) {
        const url = ApiRoutes.fileUpload.getAttachmentById(file.id);
        httpClient.open(url, '_blank');
    }
};

const WorkCardTypes = {
    Required: 1,
    RequiredCrewLead: 4,
    Optional: 2,
    NotApplicable: 3,
};

const lockedTimesheetStatuses = [
    TimesheetStatus.Approved,
    TimesheetStatus.ApprovalPending,
    TimesheetStatus.Exported,
    TimesheetStatus.Deleted,
];

const nonAdjustableTimesheetStatuses = lockedTimesheetStatuses.concat(
    TimesheetStatus.New,
    TimesheetStatus.ClientReview,
    TimesheetStatus.CancelPending,
);

export default class TimesheetDetails extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.contactFormRef = React.createRef();
        this.addressFormRef = React.createRef();
        this.meetingAddressFormRef = React.createRef();

        const stateBase = {
            loading: true,
            hasLoaded: false,
            originalDetails: '',
            originalAdjustments: JSON.stringify([]),
            details: [],
            adjustments: [],
            isPrinting: false,
            rejectionNotes: '',
            isTimesheetApprover: false,
            canViewTimesheetEquipmentAuditLogs: false,
            canCrossDispatchStaff: false,
            canCrossDispatchEquipment: false,
            currentUser: null,
            selectedContactIdType: null,
            selectedContact: null,
            newContact: null,
            showSigantureModal: false,
            selectedReviewSignature: null,
            contactFormIsReadOnly: false,
            showJobCityMissingModal: false,
            showTimesheetEquipmentLog: false,
            timesheetEquipmentLog: [],
            timesheetEquipmentLoading: false,
            showOnlyJobAssignedEquipment: true,
            isCancellingJob: false,
            showCancelJobModal: false,
            selectedJobCancellationNotes: '',
            timesheetInputs: [],
            usCounties: [],
            showRollBackApprovedModal: false,
            showRollBackExportedModal: false,
            isDeleted: false,
            driverAgreementOpen: true,
            driverAgreementDetails: [],
            preJobFormSubmission: null,
            showJotFormModal: false,
            adjustmentLoading: null,
            allowTimesheetAttachments: false,
            attachmentsLoading: false,
            paintBatchPhotos: [],
            showBillingRejectModal: false,
            selectedRowRejectionNotes: '',
            isRejectingBilling: false,
            adminRejecting: false,
            isForceReject: false,
            customFormOpened: false,
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onChange = this.onChange.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.resetForm = this.resetForm.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
    }

    componentDidMount() {
        if (this.props.show && this.props.currentTimesheetId) {
            this.open(this.props.currentTimesheetId);
        }
    }

    // #region METHODS

    onAdjustmentTimeEntryChanged = (i, ev) => {
        const { adjustments } = { ...this.state };
        const val = ev.target.value;
        const { name } = ev.target;
        // We display the options in 12-hour format, but the db has 24.
        const timeValue = ev.target.value
            ? moment(val, ['h:mm A']).format('HH:mm')
            : '';
        adjustments[i][name] = timeValue;
        this.setState({ adjustments });
        this.setAdjustmentTimeQuantity(i);
    };

    onChange = onFieldChange;

    onClose = (showConfirmationPrompt = false) => {
        if (showConfirmationPrompt) {
            const confirmed = window.confirm(
                'You have unsaved changes. Are you sure you want to leave without saving?',
            );
            if (!confirmed) {
                return; // Prevent closing if the user cancels
            }
        }

        this.resetForm();
        this.props.toggleShow(false);
        this.context.setFormOpened(false);
        this.props.onClose();
    };

    onContactFormClosed = () => this.setState({ saving: false });

    onContactFormOpened = () => this.setState({ saving: true });

    onCustomFormToggle = (open) => this.setState({ customFormOpened: open });

    onContactSavedComplete = async (e) => {
        const { details } = { ...this.state };
        const newContactDetails = { ...e };
        await this.refreshContacts();
        // select new contact in the dropdown. (details.requestorId or details.foremanId)
        details[newContactDetails.idType] = newContactDetails.id;
        this.setState({ details });
    };

    onEditContact = async (idType) => {
        const { details } = { ...this.state };
        const id = idType === 'foremanId' ? details.foremanId : details.requestorId;
        const selectedContact = await httpClient.js(
            ApiRoutes.locationContact.byId(id),
        );
        this.contactFormRef.current.resetForm();
        this.setState({
            selectedContact: { ...selectedContact },
            showContactsForm: true,
            selectedContactIdType: idType,
        });
    };

    onForemanChanged = (e) => {
        const { details } = { ...this.state };
        details.foremanId = e.value;
        this.setState({ details });
    };

    onJobCityChanged = (event) => {
        const { value } = event;
        const { details } = { ...this.state };
        details.jobCity = value;
        this.setState({ details: { ...details } });
    };

    onJobLocationChanged = (e) => {
        const { details } = { ...this.state };
        details.jobLocation = e.target.value;
        this.setState({ details });
    };

    onMeetingLocationChanged = (e) => {
        const { details } = { ...this.state };
        details.meetingLocation = e.target.value;
        this.setState({ details });
    };

    onEditJobLocation = () => {
        const { details } = { ...this.state };
        const tenant = this.context?.tenant ?? null;
        const selectedAddress = {
            ...(details.jobLocationDetails || {}),
            countryId: details.jobLocationDetails?.countryId ?? details.countryId ?? tenant?.countryId,
            countryCode: details.jobLocationDetails?.countryCode ?? details.countryCode ?? tenant?.countryCode,
            usStateId: details.jobLocationDetails?.usStateId ?? details.usStateId,
            usCountyId: details.jobLocationDetails?.usCountyId ?? details.usCountyId,
        };
        this.addressFormRef.current.open(selectedAddress, 'Edit Job Location');
    };

    onEditMeetingLocation = () => {
        const { details } = { ...this.state };
        const tenant = this.context?.tenant ?? null;

        const selectedAddress = {
            ...(details.meetingLocationDetails || {}),
            countryId: details.meetingLocationDetails?.countryId ?? details.countryId ?? tenant?.countryId,
            countryCode: details.meetingLocationDetails?.countryCode ?? details.countryCode ?? tenant?.countryCode,
            usStateId: details.meetingLocationDetails?.usStateId ?? details.usStateId,
            usCountyId: details.meetingLocationDetails?.usCountyId ?? details.usCountyId,
        };

        this.meetingAddressFormRef.current.open(selectedAddress, 'Edit Meeting Location');
    };

    onSaveMeetingLocationAddress = (address) => {
        const { details } = this.state;

        const hasChanged =
            details.meetingLocationDetails?.address1 !== address.address1 ||
            details.meetingLocationDetails?.address2 !== address.address2 ||
            details.meetingLocationDetails?.city !== address.city ||
            details.meetingLocationDetails?.state !== address.state ||
            details.meetingLocationDetails?.zip !== address.zip;
        if (hasChanged) {
            details.meetingLocationDetails = {
                ...address,
                countryId: address.countryId ?? details.countryId,
                countryCode: address.countryCode ?? details.countryCode,
                usCountyId: address.usCountyId,
                usStateId: address.usStateId,
            };
            details.meetingLocation = formatAddress(address);
            details.meetingLocationId = null;
            details.meetingLocationChanged = true;
            this.setState({ details });
        }
    };

    onSaveJobLocationAddress = (address) => {
        const { details } = this.state;

        const hasChanged =
            details.jobLocationDetails?.address1 !== address.address1 ||
            details.jobLocationDetails?.address2 !== address.address2 ||
            details.jobLocationDetails?.city !== address.city ||
            details.jobLocationDetails?.state !== address.state ||
            details.jobLocationDetails?.zip !== address.zip ||
            details.usCountyId !== address.usCountyId;
        if (hasChanged) {
            details.jobLocationDetails = {
                ...address,
                countryId: address.countryId ?? details.countryId,
                countryCode: address.countryCode ?? details.countryCode,
                usCountyId: address.usCountyId,
                usStateId: address.usStateId,
            };
            details.jobLocation = formatAddress(address);
            details.jobLocationId = null;
            details.jobLocationChanged = true;
            details.usCountyId = address.usCountyId;
            this.setState({ details });
            // Add Adjustment Badge
        }
    };

    onOrganizationContactSaved = async () => {
        this.setState({ contactFormIsReadOnly: true });

        const { selectedContact, selectedContactIdType } = { ...this.state };
        const isNew = parseInt(selectedContact.id ?? 0) <= 0;
        const result = await httpClient.andGetResponse(
            isNew ? httpClient.types.post : httpClient.types.put,
            isNew
                ? ApiRoutes.locationContact.create()
                : ApiRoutes.locationContact.update(selectedContact.id),
            { ...selectedContact },
            'Error Saving Contact',
        );

        if (result) {
            toasty.success('Contact saved.');
            const idType = selectedContactIdType;
            this.setState({
                showContactsForm: false,
                saving: false,
                selectedContact: null,
                newContact: null,
                selectedContactIdType: null,
            });
            this.onContactSavedComplete({ id: result.id, idType });
        } else {
            toasty.error('Server Error when saving Contact.');
        }

        this.setState({ contactFormIsReadOnly: false });
    };

    onPrint = () => {
        // can print from TS regular, billing or payroll specific methods
        const { isBilling, isPayroll } = { ...this.props };
        let route = ApiRoutes.report.timesheet(this.state.id);
        if (isBilling) route = ApiRoutes.report.timesheetBilling(this.state.id);
        else if (isPayroll) route = ApiRoutes.report.timesheetPayroll(this.state.id);

        httpClient.open(route, '_self');
    };

    onRefresh = () => {
        this.open(this.state.details.timesheetId);
    };

    onRequestorChanged = (e) => {
        const { details } = { ...this.state };
        details.requestorId = e.value;
        this.setState({ details });
    };

    onJobTypeChanged = (e) => {
        const { details } = { ...this.state };
        details.jobTypeId = e.value;
        this.setState({ details });
    };
    onSelectChange = onReactSelectChanged;

    onSubmit = async () => {
        // this.setState({ saving: true });
        this.setState({ saving: true });
        const { adjustments, details } = { ...this.state };

        // clean notes fields from user input before chucking them at the API.
        const notesDisplay = details.notesDisplay
            ? cleanText(details.notesDisplay)
            : null;
        const notesInternal = details.notesInternal
            ? cleanText(details.notesInternal)
            : null;
        const workOrderNum = details.workOrderNumber
            ? cleanText(details.workOrderNumber)
            : null;
        const workReq = details.workRequest
            ? cleanText(details.workRequest)
            : null;
        const purchaseOrderNum = details.purchaseOrderNumber
            ? cleanText(details.purchaseOrderNumber)
            : null;
        const jobCity = details.jobCity
            ? cleanText(details.jobCity)
            : null;
        const jobLoc = details.jobLocation
            ? cleanText(details.jobLocation)
            : null;
        const meetingLoc = details.meetingLocation
            ? cleanText(details.meetingLocation)
            : null;

        // let mtgLoc = cleanText(details.meetingLocation);

        // Per CW, allow saving past the point of approval.
        // Only editable fields post approval should be notes and print checkboxes.
        // if (!!(details.timesheetStatusId != TimesheetStatus.Approved)) {

        // Any posted charges the user wants struck
        const removals = details.timesheetDetails
            .filter((x) => !!x.removed)
            .map((x) => x.id);
        // Any strikes the user undoes
        const includes = details.timesheetDetails
            .filter((x) => !x.removed)
            .map((x) => x.id);

        // clean up the vm objects before sending.
        const adjData = [];

        adjustments.forEach((x) => {
            const adjustment = {
                timesheetId: details.timesheetId,
                id: typeof x.id === 'string' ? null : x.id,
                start: x.startDate
                    ? moment(x.startDate).format(DEFAULT_DATETIME_FORMAT)
                    : null,
                end: x.endDate ? moment(x.endDate).format(DEFAULT_DATETIME_FORMAT) : null,
                resourceid: x.resourceId ? x.resourceId : 0,
                chargeTypeId: x.chargeTypeId,
                quantity: parseFloat(x.quantity),
                notes: x.notes,
                isRentalPickup: x.isRentalPickup ?? false,
            };

            if (x.resources.length) {
                x.resources.forEach((resource) => {
                    if (resource) {
                        adjData.push({
                            ...adjustment,
                            resourceid: resource.id,
                        });
                    }
                });
            } else {
                adjData.push(adjustment);
            }
        });

        const updatedAdjustments = adjData.map((adjustment) => {
            const chargeTypeIdStr = String(adjustment.chargeTypeId);
            if (chargeTypeIdStr.includes('lunch_break')) {
                // Remove the "lunch_break" string from the id
                adjustment.chargeTypeId = adjustment.chargeTypeId.replace('lunch_break', '');
                adjustment.isLunchBreak = true;
            }
            return adjustment;
        });

        const data = {
            timesheetId: details.timesheetId,
            adjustments: updatedAdjustments,
            removals,
            includes,
            notesDisplay,
            notesInternal,
            workOrderNumber: workOrderNum,
            purchaseOrderNumber: purchaseOrderNum,
            workRequest: workReq,
            jobCity,
            foremanId: details.foremanId,
            requestorId: details.requestorId,
            jobLocation: jobLoc,
            jobLocationId: details.jobLocationId ?? null,
            jobLocationAddress: details.jobLocationDetails ?? null,
            meetingLocation: meetingLoc,
            meetingLocationId: details.meetingLocationId ?? null,
            meetingLocationAddress: details.meetingLocationDetails ?? null,
            doNotPrintNotesDisplay: details.doNotPrintNotesDisplay,
            doNotPrintFieldNotes: details.doNotPrintFieldNotes,
            usCountyId: details.usCountyId,
            isDesignatedMakeupJob: details.isDesignatedMakeupJob,
            jobTypeId: details.jobTypeId,
        };

        const url = ApiRoutes.timesheetManagement.modify(details.timesheetId);

        const response = await httpClient
            .andGetResponse(
                httpClient.types.post,
                url,
                data,
                'Failed to Update Timesheet',
            )
            .catch(this.handleSaveError);

        if (response) {
            toasty.success('Timesheet Saved');
            this.open(response);
        }
        this.setState({ saving: false });
        // }
    };

    onWorkOrderChanged = (event) => {
        const { value } = event.target;
        const { details } = { ...this.state };
        details.workOrderNumber = value;
        this.setState({ details: { ...details } });
    };
    onWorkRequestChanged = (event) => {
        const { value } = event.target;
        const { details } = { ...this.state };
        details.workRequest = value;
        this.setState({ details: { ...details } });
    };
    onPOChanged = (event) => {
        const { value } = event.target;
        const { details } = { ...this.state };
        details.purchaseOrderNumber = value;
        this.setState({ details: { ...details } });
    };

    getDeletionNote = () => {
        ConfirmDelete.show({
            title: 'Delete Timesheet',
            onSubmit: (values) => this.deleteTimesheet(values),
            body: () => (
                <Textarea
                    label="Notes"
                    id="deletionNotes"
                    name="deletionNotes"
                    required
                    placeholder="Enter notes regarding the deletion."
                    maxLength="500"
                    rows="5"
                    error="Notes are required."
                />
            ),
        });
    };

    // #endregion

    getRejectionNote = (allowDestination = false) => {
        const { details } = this.state;
        const timesheetId = details?.timesheetId;

        Modal.show({
            title: 'Reject Timesheet',
            onSubmit: (values) => this.rejectTimesheet(values),
            initialValues: allowDestination
                ? { rejectionDestination: 'flagger' }
                : {},
            SaveButtonProps: {
                label: 'Reject',
                savingLabel: 'Rejecting',
                color: 'danger',
                icon: faUndo,
            },
            body: (props) => (
                <RejectTimesheetModalBody
                    timesheetId={timesheetId}
                    allowDestination={allowDestination}
                    {...props}
                />
            ),
        });
    };

    confirmRejectTimesheet = (hasUnsavedChanges, allowDestination) => {
        if (hasUnsavedChanges) {
            const confirmed = window.confirm(
                'You have unsaved changes. Would you like to reject this timesheet anyway?',
            );

            if (!confirmed) return;
        }

        this.getRejectionNote(allowDestination);
    };

    approveTimesheetWithValidation = async () => {
        const isValid = this.formRef.current?.validate?.();
        this.setState({ formValidated: true });
        if (!isValid) {
            toasty.error('Please correct the highlighted errors.');
            return;
        }
        await this.acceptTimesheet();
    };

    editRentalDate = (detail) => {
        const isPickup = !!detail.isRentalPickup;
        const isHourly = detail.rentalUnitTypeId === RentalUnitTypes.Hourly;
        const dateLabel = isPickup ? 'Rental End' : 'Rental Start';
        // Time is only meaningful for hourly rentals; all other units are date-only.
        const tzAbbr = isHourly ? getTimeZoneAbbr(detail.rentalDateValue, this.state.details?.timeZone) : '';
        const tzSuffix = tzAbbr ? ` (${tzAbbr})` : '';

        Modal.show({
            title: `Edit ${dateLabel}${tzSuffix}`,
            initialValues: {
                rentalDate: detail.rentalDateValue ? new Date(detail.rentalDateValue) : null,
                notes: '',
                updateAll: false,
            },
            SaveButtonProps: { label: 'Save', savingLabel: 'Saving' },
            onSubmit: async ({ rentalDate, notes, updateAll }) => {
                const body = {
                    rentalDate: moment(rentalDate).format(),
                    notes,
                    updateAll: !!updateAll,
                };

                const response = await httpClient.andGetResponse(
                    httpClient.types.put,
                    ApiRoutes.timesheet.updateRentalDate(detail.id),
                    body,
                    'Failed to update rental date',
                );

                if (!response) {
                    return;
                }

                toasty.success('Rental date updated.');
                await this.getTimesheet({ showLoading: false });
            },
            body: () => (
                <>
                    <DatePicker
                        id="rentalDate"
                        name="rentalDate"
                        label={`${dateLabel}${tzSuffix}`}
                        dateFormat={isHourly ? 'MMM d, yyyy h:mm aa' : 'MMM d, yyyy'}
                        showTimeSelect={isHourly}
                        timeCaption={tzAbbr ? `Time (${tzAbbr})` : 'Time'}
                        required
                    />
                    <Textarea
                        id="notes"
                        name="notes"
                        label="Notes"
                        placeholder="Enter notes describing this change..."
                        rows={4}
                        required
                        error="Notes are required."
                    />
                    <FormCheckbox
                        id="updateAll"
                        name="updateAll"
                        labelText={`Apply this date and notes to all rentals on this timesheet (${isPickup ? 'end' : 'start'} date)`}
                    />
                </>
            ),
        });
    };

    getTimesheet = async ({ showLoading = true } = {}) => {
        const { id } = { ...this.state };

        this.setState({
            ...(showLoading ? { loading: true } : {}),
            adjustments: [],
            formValidated: false,
            rejectionNotes: '',
            errorResponse: {},
        });

        const payroll = !!this.props.isPayroll && this.props.isPayroll == true;
        const billing = !!this.props.isBilling && this.props.isBilling == true;

        const url = ApiRoutes.timesheet.slideout(id, payroll, billing);
        const tsInputs = [];
        let tsWorkCards = [];

        const [details, preJobFormSubmission] = await Promise.all([
            httpClient.js(url),
            httpClient.js(ApiRoutes.jotForms.getPreJobFormSubmission(id)),
        ]);

        let [chargeTypes, staff, equipment, customerContacts, dispatchCompanies, jobTypes] = await Promise.all([
            httpClient.js(
                ApiRoutes.chargeType.byContract(details.contractId),
            ),
            httpClient.js(
                ApiRoutes.timesheet.slideoutStaff(details.dispatchCompanyId),
            ),
            httpClient.js(
                ApiRoutes.timesheet.slideoutEquipment(
                    details.dispatchCompanyId,
                ),
            ),
            details.isSelfDispatching
                ? []
                : httpClient.js(
                    ApiRoutes.company.contacts(details.customerCompanyId),
                ),
            httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
            httpClient.js(ApiRoutes.typeAheads.jobTypes()),
        ]);

        // Duplicate chargeTypes where the first chargeTypeConfiguration has canBreak set to true, and append to the list
        const duplicatedChargeTypes = chargeTypes.reduce((acc, chargeType) => {
            acc.push(chargeType);

            if (chargeType.chargeTypeConfigurations && chargeType.chargeTypeConfigurations[0] && chargeType.chargeTypeConfigurations[0].canBreak) {
                const duplicatedChargeType = {
                    ...chargeType,
                    description: `Lunch Break (${chargeType.description})`,
                    id: `${chargeType.id}lunch_break`,
                };
                // Append the duplicated chargeType
                acc.push(duplicatedChargeType);
            }

            return acc;
        }, []);

        chargeTypes = duplicatedChargeTypes;

        const existingForemanOrRequestorIds = getExistingForemanOrRequestorIds(details);

        const customerContactsOptions = mapContacts(
            customerContacts,
            existingForemanOrRequestorIds,
        );

        this.resetForm();

        // apply filter on details
        if (this.props.onlyBillable && this.props.onlyBillable === true) {
            details.timesheetDetails = details.timesheetDetails.filter(
                (x) => x.isBillable == true,
            );
        }

        // If self dispatching//non-flagging, only a single emp should be selectable.
        if (details.isSelfDispatching) {
            const empId = [
                ...new Set(
                    details.timesheetDetails
                        .filter((td) => !!td.employeeId)
                        .map((td) => td.employeeId),
                ),
            ];
            staff = staff.filter((s) => empId.includes(s.id));
        }

        if (details?.timesheetInputQuestions.length) {
            details.timesheetInputQuestions.forEach((inQ) => {
                const answers = details.timesheetInputAnswers.filter(
                    (ia) => ia.collator.questionId == inQ.questionId,
                );
                const tsInput = {
                    question: inQ.questionName,
                    answers: answers.map((ans) => {
                        let details = '';

                        // Check if this is a chargetype question
                        if (ans.chargeTypeName) {
                            details = `${ans.chargeTypeName
                                } - from ${ans.timesheetStart
                                } to ${ans.timesheetEnd}`;
                        }

                        return {
                            employee: ans.employeeName,
                            details,
                            value: ans.answer,
                        };
                    }),
                };
                const hasDetails = tsInput.answers.some((ans) => ans.details?.trim());
                tsInput.hasDetails = hasDetails;
                tsInputs.push(tsInput);
            });
        }

        if (details?.employeeWorkCards.length) {
            tsWorkCards = details.employeeWorkCards.map((empWorkCard) => ({
                empId: empWorkCard.employeeId,
                empName: empWorkCard.employeeName,
                workCardUrl: empWorkCard.imagePath,
                workCardModifiedBy: empWorkCard.modifiedBy,
                workCardModifiedOn: empWorkCard.modifiedOn,
                timezone: empWorkCard.timeZone,
            }));
        }

        const { tenant, permissions } = this.context;
        const { tenantSettings } = tenant;

        const hasAuditLogsPermission = tenantSettings.timesheetEquipmentAuditEnabled & permissions.includes(ApplicationPermissions.timesheet_equipment_audit_logs_view);

        const isApprover = lockedTimesheetStatuses.includes(details.timesheetStatusId)
            ? permissions.includes(ApplicationPermissions.timesheet_edit_approved)
            : permissions.includes(ApplicationPermissions.timesheet_accept);
        if (details.cancellationNotes?.length) {
            const notes = details.cancellationNotes.map((note) => {
                const iana = convertWinToIANA(details.timeZone);
                const cancelTime = appendTimeZone(moment.parseZone(note.cancelledOnOffset).format(MomentDateFormat.DateTimeShortAnte), note.cancelledOnOffset, details.timeZone);
                const createdTime = appendTimeZone(moment.utc(note.createdOnUTC).tz(iana).format(MomentDateFormat.DateTimeShortAnte), note.createdOnUTC, details.timeZone);

                if (note.noteType === CancellationNoteTypes.cancellation) {
                    return `Cancelled On: ${cancelTime}, Created On: ${createdTime}.
                    Notes: ${note.note}${note.cancelContact}`;
                }
                if (note.noteType === CancellationNoteTypes.auditApplied) {
                    return `Cancelled On: ${cancelTime}, Created On: ${createdTime} by ${note.createdBy} - ${note.note || 'No note provided'}`;
                } else {
                    return `Cancel Removed On: ${createdTime} by ${note.createdBy}`;
                }
            });
            details.cancellationNotes = notes;
        }

        let paintBatchPhotos = [];
        if (tenantSettings.enablePaintBatchCodes) {
            try {
                paintBatchPhotos = await httpClient.get(
                    ApiRoutes.timesheet.getAttachments(id, TimesheetAttachmentScope.PaintBatchPhotos),
                );
            } catch {
                paintBatchPhotos = [];
            }
        }

        this.setState({
            originalDetails: JSON.stringify(details),
            details,
            loading: false,
            hasLoaded: true,
            chargeTypes: chargeTypes.filter((x) => !x.payrollChargeTypeId || x.payrollChargeTypeId === PayrollChargeTypes.PerDiem),
            staff,
            equipment,
            dispatchCompanies,
            customerContactsOptions,
            timesheetInputs: tsInputs,
            preJobFormSubmission,
            timesheetWorkCards: tsWorkCards,
            isTimesheetApprover: isApprover,
            canViewTimesheetEquipmentAuditLogs: hasAuditLogsPermission,
            jobTypeOptions: [...jobTypes],
            paintBatchPhotos,
        });
    };

    getStaffForCompany = (companyId) => this.state.staff.filter((x) => x.dispatchCompanyId === companyId);
    getEquipmentForCompany = (companyId) => this.state.equipment.filter((x) => x.dispatchCompanyId === companyId);

    getTimesheetEquipmentLogs = () => {
        const { details } = this.state;

        Alert.show({
            title: 'Timesheet Equipment Log',
            size: 'xl',
            body: (
                <EquipmentLogModalBody
                    timesheetId={details.timesheetId}
                    jobStartTimeOffset={details.jobStartTimeOffset}
                    timeZone={details.timeZone}
                />
            ),
        });
    };

    setAdjustmentTimeQuantity = (i) => {
        const { details, adjustments } = { ...this.state };
        let adj = new TimesheetDetailsAdjustment();
        adj = adjustments[i];

        // Calculate the duration between Start and End.  Factor in if this enters into the next day.
        // Use the job start date/time as opposed to moment defaulting to the current day when getting duration.
        if (adj.start && adj.end) {
            const time_format = 'HH:mm';
            const date_format = 'YYYY-MM-DD';
            const mStartDate = moment(details.jobStartTimeRaw);
            const mEndDate = moment(details.jobStartTimeRaw);

            const mStartTime = moment(adj.start, time_format);
            const mEndTime = moment(adj.end, time_format);
            if (mEndTime.isBefore(mStartTime)) {
                mEndTime.add(1, 'day');
                mEndDate.add(1, 'day');
            }

            const dur = moment.duration(mEndTime.diff(mStartTime));

            adj.startDate = moment(
                `${mStartDate.format(date_format)} ${mStartTime.format(
                    time_format,
                )}`,
            ).valueOf();
            adj.endDate = moment(
                `${mEndDate.format(date_format)} ${mEndTime.format(
                    time_format,
                )}`,
            ).valueOf();
            adj.quantity = dur.asHours().toFixed(2);

            adjustments[i] = adj;

            this.setState({ adjustments });
        } else if (adj.start) {
            const time_format = 'HH:mm';
            const date_format = 'YYYY-MM-DD';
            const mStartDate = moment(details.jobStartTimeRaw);

            const mStartTime = moment(adj.start, time_format);

            adj.startDate = moment(
                `${mStartDate.format(date_format)} ${mStartTime.format(
                    time_format,
                )}`,
            ).valueOf();

            adjustments[i] = adj;

            this.setState({ adjustments });
        }
    };

    acceptTimesheet = async () => {
        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};
        this.clearSaveErrors();
        if (
            tenantSettings.timesheetUseJobCity
            && !this.state.details.isSelfDispatching
        ) {
            const { jobCity } = this.state.details;
            if (jobCity == null || jobCity.length == 0) {
                Alert.show({
                    title: 'Approval Error',
                    body: 'Job City must have a value before approving.',
                });

                if (document.getElementById('jobCity')) {
                    document.getElementById('jobCity').focus();
                }

                return;
            }
        }

        if (this.getCountyCaptureSetting(tenantSettings)) {
            const { usCountyId } = this.state.details;

            if (usCountyId == null) {
                toasty.error('A county selection is required for this timesheet');

                return;
            }
        }

        if (this.state.details.contractAutoCalculateDriveTimeBilling) {
            const { jobLocationDetails } = this.state.details;
            if (!jobLocationDetails || jobLocationDetails.length === 0) {
                toasty.error('A geocoded job location is required. Click Edit and enter the full address.');
                return;
            }

            const { meetingLocationDetails } = this.state.details;
            if (!meetingLocationDetails || meetingLocationDetails.length === 0) {
                toasty.error('A geocoded meeting location is required. Click Edit and enter the full address.');
                return;
            }
        }

        if (this.state.saving) return false;
        this.setState({ saving: true });

        const { timesheetId } = this.state.details;
        try {
            await this.onSubmit();

            this.setState({ saving: true });
            const response = await httpClient.put(
                ApiRoutes.timesheetManagement.accept(timesheetId),
                null,
                httpClient.format.none,
            );
            this.setState({ saving: false });
            if (response.ok) {
                const responseJson = await response.json();

                if (responseJson != 'Timesheet Approved') {
                    toasty.success('Timesheet Approval Pending');
                    await this.open(timesheetId);
                    const serviceResponse = {
                        title: 'Approval Pending',
                        errors: {
                            Warning: [responseJson],
                        },
                    };
                    this.displaySaveErrors(serviceResponse);
                } else {
                    toasty.success('Timesheet Approved');
                    this.open(timesheetId);
                }
            } else {
                toasty.error('Error Approving Timesheet');
                const responseJson = await response.json();
                const serviceResponse = {
                    title: 'Error Approving Timesheet',
                    errors: {
                        Error: [responseJson.message],
                    },
                };
                this.displaySaveErrors(serviceResponse);
            }
        } catch (err) {
            console.error(err);
            toasty.error('Error Approving Timesheet');
        } finally {
            this.setState({ saving: false });
        }
    };

    deleteTimesheet = async ({ deletionNotes }) => {
        let success = true;

        const { details } = { ...this.state };

        // Submit the timesheet if we have the note filled out.
        const { timesheetId } = details;
        const data = {
            timesheetId,
            note: deletionNotes,
        };

        let error = '';
        try {
            await httpClient.andGetResponse(
                httpClient.types.post,
                ApiRoutes.timesheetManagement.softDelete(),
                data,
                'Error Soft Deleting Timesheet',
            );
        } catch (ex) {
            error = ex;
            success = false;
        }

        if (success) {
            toasty.success('Timesheet deleted.');
        } else {
            toasty.error(`Error Soft Deleting Timesheet: ${error}`);
        }

        this.onClose();
    };

    restoreTimesheet = async () => {
        let success = true;
        const { details } = { ...this.state };
        const { timesheetId } = details;
        let error = '';
        try {
            await httpClient.andGetResponse(
                httpClient.types.post,
                ApiRoutes.timesheetManagement.restoreTimesheet(timesheetId),
                'Error Restoring Deleted Timesheet',
            );
        } catch (ex) {
            error = ex;
            success = false;
        } finally {
            this.setState({
                saving: false,
            });
        }

        if (success) {
            toasty.success('Timesheet restored.');
        } else {
            toasty.error(`Error in Restoring Timesheet: ${error}`);
        }

        this.onClose();
    };
    groupTimesheetDetails = () => {
        const { details } = this.state;
        const { timesheetDetails } = details;

        let i = 0;
        let totalAFADs = 0;
        const len = (timesheetDetails ?? []).length;

        const results = [];

        for (; i < len; i += 1) {
            const detail = timesheetDetails[i];
            let description = detail.resourceName;

            const resources = [detail];

            if (detail.isAFAD) {
                totalAFADs += 1;

                if (detail.numAFADsInSet > 1) {
                    // Group everything under the first item in the set and discard all other items in the set
                    const isFirstInSet = totalAFADs % detail.numAFADsInSet == 1;

                    if (!isFirstInSet) {
                        continue;
                    }

                    let j = 1;

                    for (; j < detail.numAFADsInSet; j += 1) {
                        const next = timesheetDetails[i + j];

                        if (!next?.isAFAD || next.isBreak !== detail.isBreak) {
                            totalAFADs = 0; // Reset counter on invalid entry
                            break;
                        }

                        description = `${description}, ${next.resourceName}`;
                        resources.push(next);
                    }
                }
            }

            results.push({ ...detail, resources, resourceName: description });
        }

        return results;
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    // #region CONTACTS EDITING

    open = (timesheetId) => {
        // reset adjustments and validation on the way back in.
        this.props.toggleShow(true);
        this.context.setFormOpened(true);

        this.setState({ id: parseFloat(timesheetId) }, async () => {
            await Promise.all([
                this.populateState(),
                this.getTimesheet(),
            ]);
        });
    };

    async populateState() {
        // Bail out if this has already run
        if (this.state.usCounties.length > 0) {
            return;
        }

        const { tenant, permissions } = this.context;
        const { tenantSettings } = tenant;

        const [usCounties] = await Promise.all([
            httpClient.js(ApiRoutes.USCounties.all()),
        ]);

        this.setState({
            usCounties,
            canCrossDispatchStaff: Boolean(tenantSettings.allowCrossDispatching) && permissions.includes(ApplicationPermissions.dispatch_employee),
            canCrossDispatchEquipment: Boolean(tenantSettings.allowCrossDispatching) && permissions.includes(ApplicationPermissions.dispatch_equipment),
            allowTimesheetAttachments: Boolean(tenantSettings && tenantSettings.allowTimesheetAttachments),
        });
    }

    refreshContacts = async () => {
        const { details } = { ...this.state };
        const customerContacts = await httpClient.js(
            ApiRoutes.company.contacts(details.customerCompanyId),
        );

        const existingForemanOrRequestorIds = getExistingForemanOrRequestorIds(details);

        const options = mapContacts(
            customerContacts,
            existingForemanOrRequestorIds,
        );
        this.setState({ customerContactsOptions: options });
    };

    rejectTimesheet = async ({
        rejectionNotes, rejectionReason, linkedFlagId, rejectionDestination,
    }) => {
        let success = false;
        let errorMessage = null;

        const { details } = { ...this.state };

        // Submit the timesheet if we have the note filled out.
        const { timesheetId } = details;
        const data = {
            timesheetId,
            note: rejectionNotes,
            rejectionReasonId: Number(rejectionReason),
            linkedFlagId: linkedFlagId ? Number(linkedFlagId) : null,
            rejectToOperations: rejectionDestination === 'operations',
        };

        try {
            const response = await httpClient.post(
                ApiRoutes.timesheetManagement.reject(),
                data,
                httpClient.format.none,
            );
            if (response.redirected) window.location.href = response.url;
            else if (response.ok) success = true;
            else {
                try {
                    const responseData = await response.json();
                    errorMessage = responseData?.message || null;
                } catch {
                    errorMessage = null;
                }
            }
        } catch (ex) {
            console.error(ex);
            success = false;
            errorMessage = ex?.message || null;
        }

        if (success) {
            toasty.success('Timesheet rejected.');
            await this.getTimesheet();
        } else {
            toasty.error(
                errorMessage
                    || 'There were errors when rejecting the timesheet. Please try your submission again or contact support for assistance.',
            );
        }

        return null;
    };

    removeCharge = (resources) => {
        const { details } = this.state;

        const timesheetDetails = resources.reduce((results, resource) => {
            const index = results.findIndex((x) => x.id === resource.id);

            results[index] = {
                ...results[index],
                removed: !results[index].removed,
                show: !results[index].isAdjustment,
            };

            return results;
        }, details.timesheetDetails);

        this.setState({ details: { ...details, timesheetDetails } });
    };

    resetForm = () => {
        this.setState({
            formValidated: false,
        });
    };

    handleDesignatedMakeupJobChange = () => {
        const { details } = this.state;
        details.isDesignatedMakeupJob = !details.isDesignatedMakeupJob;

        this.setState({ details });
    };

    showRollBackApprovedModal = () => {
        const { details } = this.state;

        Modal.show({
            title: 'Reset Approved Time Charge?',
            onSubmit: () => this.rollBackTimesheet(),
            SaveButtonProps: {
                label: 'Reset',
                color: 'warning',
                icon: faSync,
            },
            body: () => (
                <>
                    Are you sure you want to reset timesheet
                    {' '}
                    {details.timesheetNumber}
                    {' '}
                    to a Submitted state?
                </>
            ),
        });
    };

    rollBackTimesheet = async () => {
        this.setState({ saving: true });
        const { details } = this.state;
        const { timesheetId } = details;

        try {
            const response = await httpClient.andGetResponse(
                httpClient.types.post,
                ApiRoutes.timesheet.rollBackApproved(timesheetId),
                null,
                'Error Resetting Timesheet',
            );

            if (response) {
                toasty.success('Timesheet Reset to Submitted');
                this.open(timesheetId);
            }
        } finally {
            this.setState({ saving: false, showRollBackApprovedModal: false });
        }
    };

    getCountyCaptureSetting = (tenantSettings) => (
        (tenantSettings.captureCounty == CountyCaptureType.AllTimesheets && this.state.details.jobId)
        || (tenantSettings.captureCounty == CountyCaptureType.PrevailingWageTimesheets && this.state.details.contractIsPrevailingWage)
        || (tenantSettings.captureCounty == CountyCaptureType.UnionTimesheets && this.state.details.contractUnionWorkTypeId)
    );

    clearSaveErrors = () => this.setState({ errorResponse: {} });

    displaySaveErrors = (response) => this.setState({ errorResponse: response });

    onAdjustmentDispatchCompanyChanged = async (index, { value: id }, isStaff) => {
        const { adjustments, staff, equipment } = this.state;

        adjustments[index].dispatchCompanyId = id;

        this.setState({ adjustments, adjustmentLoading: index });

        if (isStaff) {
            const newStaff = await httpClient.js(
                ApiRoutes.timesheet.slideoutStaff(id),
            );

            this.setState({
                staff: uniqBy(staff.concat(newStaff), (person) => person.id),
                adjustmentLoading: null,
            });
        } else {
            const newEquipment = await httpClient.js(
                ApiRoutes.timesheet.slideoutEquipment(id),
            );

            this.setState({
                equipment: uniqBy(equipment.concat(newEquipment), (item) => item.id),
                adjustmentLoading: null,
            });
        }
    };

    shouldShowWorkCard = () => {
        const { details } = this.state;
        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};
        // First check if tenant has work cards enabled
        if (!tenantSettings?.showWorkCardOnTimesheet) {
            return false;
        }

        // Then check contract setting exists
        if (!details?.workCardTypeId) {
            return false;
        }

        // Show if either Optional or Required
        return parseInt(details.workCardTypeId) !== WorkCardTypes.NotApplicable;
    };

    isWorkCardRequired = () => {
        const { details } = this.state;

        if (details?.isEmergency) {
            return false;
        }

        return parseInt(details?.workCardTypeId) === WorkCardTypes.Required;
    };

    renderLocationDetails(
        details,
        timesheetApproved,
        isTimesheetApprover,
        countyCaptureEnabled,
        usCounties,
    ) {
        const county = getCounty(usCounties, {
            usCountyId: details?.usCountyId,
            jobLocationCountyId: details?.jobLocationCountyId,
            contractUnionWorkTypeId: details?.contractUnionWorkTypeId,
        });

        const canEditLocation = !(timesheetApproved && !isTimesheetApprover);

        return (
            <div className="locationDetails">
                <FormGroup className="mb-2">
                    <FormLabel
                        htmlFor="meetingLocation"
                        label="Meeting Location"
                        className="bottom-border"
                    />
                    <div className="d-flex flex-row w-100 align-items-start">
                        {!!(details ?? {}).meetingLocationUrlFormatted && (
                            <a
                                rel="noopener noreferrer"
                                className="text-white mr-2 btn btn-sm btn-success p-1"
                                title="Open in Google Maps"
                                href={getMapUrl((details ?? {}).meetingLocationUrlFormatted)}
                                target="_blank"
                            >
                                <i className="fa fa-map-marked-alt" />
                            </a>
                        )}
                        <div className="adj-indicator">
                            {!!details?.meetingLocationChanged && (
                                <Badge
                                    title="This job location has been adjusted."
                                    color="warning"
                                    className="adj-badge"
                                    style={{
                                        fontSize: '100% !important',
                                    }}
                                >
                                    ADJ
                                </Badge>
                            )}
                        </div>
                        <div className="d-flex flex-column flex-fill">
                            <FlexStartRow>
                                <FormLabel
                                    htmlFor="meetingLocation"
                                    label="Address"
                                />
                                <Input
                                    style={{
                                        minWidth: '600px',
                                    }}
                                    autoComplete="off"
                                    required
                                    id="meetingLocation"
                                    maxLength="100"
                                    displayMaxLength={false}
                                    name="meetingLocation"
                                    className={cls(
                                        'p-1 form-control form-control-sm w-auto',
                                        {
                                            'form-control-readonly':
                                                !!timesheetApproved,
                                        },
                                    )}
                                    readOnly
                                    disabled={!canEditLocation}
                                    value={details?.meetingLocation ?? ''}
                                    endAddon={(
                                        <>
                                            {canEditLocation
                                                && details.contractAutoCalculateDriveTimeBilling
                                                && (!details.meetingLocationDetails?.id && !details.meetingLocationDetails) && (
                                                    <>
                                                        <FontAwesomeIcon
                                                            id="invalid-meeting-location"
                                                            icon={faExclamationTriangle}
                                                            className="text-warning mb-2"
                                                        />
                                                        <UncontrolledTooltip target="invalid-meeting-location">
                                                            A geocoded address is required for approval. Click Edit to enter the full address.
                                                        </UncontrolledTooltip>
                                                    </>
                                                )}
                                        </>
                                    )}
                                />

                                {canEditLocation && (
                                    <Button
                                        color="link"
                                        size="sm"
                                        className="site-link ml-4 mt-1"
                                        onClick={this.onEditMeetingLocation}
                                    >
                                        Edit
                                    </Button>
                                )}
                            </FlexStartRow>

                            {!!details?.meetingLocationChanged && (
                                <small className="mt-1">
                                    <strong className="mr-1 text-muted">
                                        Initial Meeting Location:
                                    </strong>
                                    {`${details?.initialMeetingLocation}`}
                                </small>
                            )}
                        </div>
                    </div>
                </FormGroup>

                <FormGroup>
                    <FormLabel
                        htmlFor="jobLocation"
                        label="Job Location"
                        className="bottom-border"
                    />
                    <div className="d-flex flex-row w-100 align-items-start">
                        {!!(details ?? {}).jobLocationUrlFormatted && (
                            <a
                                rel="noopener noreferrer"
                                className="text-white mr-2 btn btn-sm btn-success p-1"
                                title="Open in Google Maps"
                                href={getMapUrl((details ?? {}).jobLocationUrlFormatted)}
                                target="_blank"
                            >
                                <i className="fa fa-map-marked-alt" />
                            </a>
                        )}
                        <div className="adj-indicator">
                            {!!details?.jobLocationChanged && (
                                <Badge
                                    title="This job location has been adjusted."
                                    color="warning"
                                    className="adj-badge"
                                    style={{
                                        fontSize: '100% !important',
                                    }}
                                >
                                    ADJ
                                </Badge>
                            )}
                        </div>
                        <div className="d-flex flex-column flex-fill">
                            <FlexStartRow>
                                <FormLabel
                                    htmlFor="jobLocation"
                                    label="Address"
                                />
                                <Input
                                    style={{
                                        minWidth: '600px',
                                    }}
                                    autoComplete="off"
                                    required
                                    id="jobLocation"
                                    maxLength="100"
                                    displayMaxLength={false}
                                    name="jobLocation"
                                    className={cls(
                                        'p-1 form-control form-control-sm w-auto',
                                        {
                                            'form-control-readonly':
                                                !!timesheetApproved,
                                        },
                                    )}
                                    readOnly
                                    disabled={!canEditLocation}
                                    value={details?.jobLocation ?? ''}
                                    endAddon={(
                                        <>
                                            {canEditLocation
                                                && details.contractAutoCalculateDriveTimeBilling
                                                && (!details.jobLocationDetails?.id && !details.jobLocationDetails) && (
                                                    <>
                                                        <FontAwesomeIcon
                                                            id="invalid-job-location"
                                                            icon={faExclamationTriangle}
                                                            className="text-warning mb-2"
                                                        />
                                                        <UncontrolledTooltip target="invalid-job-location">
                                                            A geocoded address is required for approval. Click Edit to enter the full address.
                                                        </UncontrolledTooltip>
                                                    </>
                                                )}
                                        </>
                                    )}
                                />

                                {canEditLocation && (
                                    <Button
                                        color="link"
                                        size="sm"
                                        className="site-link ml-4 mt-1"
                                        onClick={this.onEditJobLocation}
                                    >
                                        Edit
                                    </Button>
                                )}
                            </FlexStartRow>

                            {!!details?.jobLocationChanged && (
                                <small className="mt-1">
                                    <strong className="mr-1 text-muted">
                                        Initial Job Location:
                                    </strong>
                                    {`${details?.initialJobLocation}`}
                                </small>
                            )}
                        </div>
                    </div>
                </FormGroup>

                {Boolean(countyCaptureEnabled) && (
                    <FormGroup className="mt-2">
                        <FormLabel
                            htmlFor="usCounty"
                            text={`Job County ${getCountyName(usCounties, details)}`}
                            className="bottom-border"
                        />
                        <div className="d-flex flex-row w-100 align-items-center">
                            <div className="adj-indicator">
                                {Boolean(details.usCountyId && details.usCountyId !== details.jobLocationCountyId) && (
                                    <Badge
                                        color="warning"
                                        className="adj-badge"
                                    >
                                        ADJ
                                    </Badge>
                                )}
                            </div>

                            <FormLabel
                                htmlFor="usCountyId"
                                label="County"
                                className="control-label"
                            />
                            <span>
                                <CountyLabel
                                    {...county}
                                />
                            </span>
                        </div>
                    </FormGroup>
                )}
            </div>
        );
    }

    toggleJotFormModal = () => {
        const { showJotFormModal } = this.state;
        this.setState({ showJotFormModal: !showJotFormModal });
    };

    onUploadComplete = async (uploads) => {
        this.setState({ attachmentsLoading: true });
        if (uploads?.length) {
            const { id } = this.state;
            try {
                const attachmentIds = uploads.map((upload) => upload.id);
                await httpClient.put(
                    ApiRoutes.timesheet.updateAttachments(id),
                    attachmentIds);
                toasty.success('Attachments updated successfully');
            } catch (error) {
                toasty.error('Failed to update attachments');
                console.error('Error updating attachments:', error);
            }
        }
        await this.setAttachments();
        this.setState({ attachmentsLoading: false });
    };

    onFileDelete = async (attachment) => {
        if (!attachment) return;
        const { id, details } = this.state;
        const attachments = details?.attachments?.filter((a) => a.id !== attachment.id) ?? [];
        this.setState({
            details: {
                ...details,
                attachments,
            },
        });
        try {
            const response = await httpClient.delete(
                ApiRoutes.timesheet.deleteAttachment(id, attachment.id),
            );
            if (response) {
                await this.setAttachments();
                toasty.success('Attachment deleted');
            }
        } catch (error) {
            toasty.error('Failed to delete attachment');
            console.error('Error deleting attachment:', error);
        }
    };

    setAttachments = async () => {
        const { id, details } = this.state;
        const attachments = await httpClient.get(
            ApiRoutes.timesheet.getAttachments(id),
        );
        this.setState({
            details: {
                ...details,
                attachments,
            },
        });
    };

    toggleBillingRejectModal = async () => {
        const { details } = this.state;

        if (!details?.billingId) {
            toasty.error('Unable to reject: missing billing record.');
            return;
        }

        const adminRequired = await httpClient.get(
            ApiRoutes.billingCharge.needsAdminReject(details.billingId),
        ).catch(() => false);

        if (adminRequired === false) {
            this.showBillingRejectModal(false);
            return;
        }

        if (adminRequired === true && !this.context?.user?.isAdmin) {
            toasty.info('An admin needs to reject this timesheet due to it being approved already');
            return;
        }

        if (adminRequired === true && this.context?.user?.isAdmin) {
            this.showBillingRejectModal(true);
        }
    };

    onBillingRejectClicked = async () => {
        const { details } = this.state;

        if (details?.invoiceId && details.invoiceId > 0) {
            toasty.error('This timesheet has already been invoiced and cannot be rejected.');
            return;
        }

        await this.toggleBillingRejectModal();
    };

    adminReject = async (model) => {
        const response = await httpClient
            .post(ApiRoutes.billingCharge.adminReject(), model)
            .catch(this.handleSaveError);

        if (response) toasty.success('Billing: Charges Rejected');
        this.resetBillingRejectState();
    };

    forcedReject = async (model) => {
        const response = await httpClient
            .post(ApiRoutes.billingCharge.forceReject(), model)
            .catch(this.handleSaveError);

        if (response) toasty.success('Billing: Charges Rejected');
        this.resetBillingRejectState();
    };

    defaultReject = async (model) => {
        const response = await httpClient
            .post(ApiRoutes.billingCharge.reject(), model)
            .catch(this.handleSaveError);

        if (response) toasty.success('Billing: Charges Rejected');
        this.resetBillingRejectState();
    };

    continueClicked = async () => {
        const { pendingForceRejectModel } = this.state;
        if (pendingForceRejectModel) {
            await this.forcedReject(pendingForceRejectModel);
        }
    };

    resetBillingRejectState = async () => {
        this.setState({
            showBillingRejectModal: false,
            selectedRowRejectionNotes: '',
            isRejectingBilling: false,
            adminRejecting: false,
            isForceReject: false,
            pendingForceRejectModel: null,
        });

        await this.getTimesheet();
    };

    onSubmitBillingReject = async ({ selectedRowRejectionNotes }, adminRejecting) => {
        const {
            details, isForceReject,
        } = this.state;

        if (!selectedRowRejectionNotes?.trim()) return;
        if (this.state.isRejectingBilling) return;

        const alreadyInvoiced = Boolean(details?.invoiceNumber) || Boolean(details?.invoicedOn);
        if (alreadyInvoiced) {
            toasty.error('This timesheet has already been invoiced and cannot be rejected.');
            return;
        }

        this.setState({ isRejectingBilling: true });

        const forcedPermission = this.context.checkUserHasPermission(
            ApplicationPermissions.billing_charge_force_reject,
        );

        const model = {
            billingId: details.billingId,
            note: selectedRowRejectionNotes,
        };

        const forceRequired = await httpClient
            .get(ApiRoutes.billingCharge.needsForceReject(model.billingId))
            .catch(() => false);

        if (adminRejecting === true) {
            await this.adminReject(model);
            return;
        }

        if (forcedPermission && forceRequired) {
            if (isForceReject) {
                await this.forcedReject(model);
                return;
            }

            // Store the model so continueClicked can call forcedReject directly
            this.setState({
                pendingForceRejectModel: model,
            }, () => {
                toasty.action(
                    'Unable to Proceed',
                    'Billing Record(s) have saved edits',
                    'Continue Anyway',
                    this.continueClicked,
                    this.resetBillingRejectState,
                );
            });
            return;
        }

        await this.defaultReject(model);
    };
    // #endregion

    handleUploadComplete = async (empId) => {
        const { details } = this.state;
        await this.getWorkCard(details?.timesheetId, empId);
    };

    getWorkCard = async (timesheetId, selectedEmployeeId) => {
        try {
            const workCard = await httpClient.js(
                ApiRoutes.timesheetReview.getEmployeeWorkCard(timesheetId, selectedEmployeeId),
            );

            this.setState((prev) => ({
                timesheetWorkCards: (prev.timesheetWorkCards || []).map((card) =>
                    card.empId === selectedEmployeeId
                        ? {
                            ...card,
                            workCardUrl: workCard?.imagePath,
                            workCardModifiedBy: workCard?.modifiedBy,
                            workCardModifiedOn: workCard?.modifiedOn,
                            timeZone: workCard?.timeZone,
                        }
                        : card,
                ),
            }));
        } catch (error) {
            console.error('Error fetching work card:', error);
        }
    };

    renderEquipmentLogTable = () => {
        const { details } = this.state;
        const filteredLog = this.state.showOnlyJobAssignedEquipment
            ? this.state.timesheetEquipmentLog.filter((log) => log.assignedToJob)
            : this.state.timesheetEquipmentLog;

        return (
            <table className="table table-sm table-bordered table-striped timesheet-details-table">
                <thead className="text-muted">
                    <tr>
                        <th className="auditVehicle">Time</th>
                        <th className="auditVehicle">Equipment</th>
                        <th className="auditDriver">Assigned To</th>
                        <th className="auditCreated">Event</th>
                        <th className="auditPlaces">Places</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredLog.length === 0 ? (
                        <tr>
                            <td
                                colSpan="5"
                                className="text-center"
                            >
                                No logs found.
                            </td>
                        </tr>
                    ) : (
                        filteredLog.map((logEntry, index) => {
                            const time = moment(logEntry.createdOn).format('MMM DD, h:mm a');
                            const arrivalTime = appendTimeZone(time, details.jobStartTimeOffset, details.timeZone);

                            return (
                                <tr key={index}>
                                    <td>{arrivalTime}</td>
                                    <td>
                                        {logEntry.equipmentIdentifier}

                                        <Can
                                            I="view"
                                            a="equipment"
                                        >
                                            <Link
                                                to={`${AppNavPaths.Equipment}?eId=${logEntry.equipmentId}`}
                                                target="_blank"
                                                className="ml-1"
                                            >
                                                <FontAwesomeIcon
                                                    icon={faArrowUpRightFromSquare}
                                                    size="sm"
                                                />
                                            </Link>
                                        </Can>
                                    </td>
                                    <td>
                                        {logEntry.employeeName}

                                        <Can
                                            I="view"
                                            a="employee"
                                        >
                                            <Link
                                                to={`${AppNavPaths.Employee}/${logEntry.employeeId}`}
                                                target="_blank"
                                                className="ml-1"
                                            >
                                                <FontAwesomeIcon
                                                    icon={faArrowUpRightFromSquare}
                                                    size="sm"
                                                />
                                            </Link>
                                        </Can>
                                    </td>
                                    <td>{logEntry.message}</td>
                                    <td>
                                        <AuditPlaces {...logEntry} />
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        );
    };

    showBillingRejectModal = (adminRejecting) => {
        Modal.show({
            title: 'Billing: Reject Charges',
            onSubmit: (values) => this.onSubmitBillingReject(values, adminRejecting),
            SaveButtonProps: {
                label: 'Reject',
                savingLabel: 'Rejecting',
                color: 'danger',
                icon: faUndo,
            },
            body: () => (
                <>
                    {adminRejecting ? (
                        <small
                            style={{ marginBottom: '20px' }}
                            className="form-text text-danger"
                        >
                            This administrative action will
                            delete any approved payroll or
                            billing records, and reset the
                            timesheet. Only administrators will
                            be allowed to adjust and re-approve
                            this timesheet. Please take note of
                            Payroll and Billing implications.
                        </small>
                    ) : (
                        <FormText
                            style={{ marginBottom: '20px' }}
                        >
                            This action will reject the
                            timesheet from billing review and
                            remove all charges from payroll
                            review.
                        </FormText>
                    )}

                    <Textarea
                        label="Notes"
                        id="selectedRowRejectionNotes"
                        name="selectedRowRejectionNotes"
                        required
                        placeholder="Enter notes regarding the rejection."
                        maxLength="500"
                        rows="5"
                        error="Notes are required."
                    />
                </>
            ),
        });
    };

    render() {
        const {
            originalDetails,
            originalAdjustments,
            details,
            adjustments,
            formValidated,
            validationMessage,
            loading,
            isPrinting,
            saving,
            chargeTypes,
            isTimesheetApprover,
            canViewTimesheetEquipmentAuditLogs,
            customerContactsOptions,
            selectedContact,
            showContactsForm,
            contactFormIsReadOnly,
            usCounties,
            timesheetInputs,
            preJobFormSubmission,
            showJotFormModal,
            timesheetWorkCards,
            canCrossDispatchEquipment,
            canCrossDispatchStaff,
            dispatchCompanies,
            adjustmentLoading,
            jobTypeOptions,
            hasLoaded,
            allowTimesheetAttachments,
            attachmentsLoading,
            timesheetEquipmentLoading,
            customFormOpened,
            paintBatchPhotos,
        } = this.state;
        const {
            isBilling, isPayroll, isTimesheet, isTimesheetForm, showNav,
        } = { ...this.props };

        const groups = details.timesheetStatusChanges;
        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};
        const s_Details = JSON.stringify(details);
        const s_Adjustments = JSON.stringify(adjustments);

        const timesheetApproved = lockedTimesheetStatuses.includes(details?.timesheetStatusId);
        const workCardLocked = Boolean(details?.invoiceId);

        const hasTimesheetAuditLogs = canViewTimesheetEquipmentAuditLogs && !details?.isSelfDispatching && !details?.isPayrollCharge;

        const countyCaptureEnabled = this.getCountyCaptureSetting(tenantSettings);

        const timesheetStatusRollbackAllowed = tenantSettings.allowTimesheetStatusRollbacks && !details?.isPayrollCharge && !isBilling && !isPayroll;

        const isEquipmentOnly = details.timesheetDetails?.length > 0
            && !details.timesheetDetails.some((td) => td.employeeId);

        const showConfirmationPrompt = !loading
            && !saving
            && (!isEqual(originalDetails, s_Details)
                || !isEqual(originalAdjustments, s_Adjustments));

        let jobDetailsHeader = details?.isSelfDispatching
            ? 'Work and Personnel Details'
            : 'Job Details';

        if (
            tenantSettings.showContractDescriptionInTimesheetView
            && !details.isSelfDispatching
            && details.jobName
        ) {
            jobDetailsHeader = `Job Details - ${details.jobName}`;
        }
        const { errorResponse } = { ...this.state };
        const jobStartTime = details?.jobStartTime
            ? appendTimeZone(details.jobStartTime, details.jobStartTimeOffset, details.timeZone)
            : undefined;

        const { showTimesheetStatusHistory } = tenantSettings;

        const combinedInvoiceEnabled = Boolean(tenantSettings?.combineRentalNonRentalChargesOnRentalInvoices);
        const showBillingReject = combinedInvoiceEnabled && timesheetApproved;

        const supportedFiles = SupportedFileExtensions.image;
        return (
            <>
                <FormPrompt
                    when={!!showConfirmationPrompt}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <SlideForm
                    size="col-xl-12 col-md-12 col-xs-12"
                    loading={loading || !tenantSettings}
                    show={this.props.show}
                    id="timesheetForm"
                    formIcon={faUserClock}
                    formTitle="Timesheet Details"
                    formRef={this.formRef}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    isValid={(value) => {
                        if (!value) {
                            toasty.error(
                                'Validation Failed. Please review and complete all required data',
                            );
                        }
                    }}
                    className="pb-2 w-100"
                    hideToolbar
                    fullScreen
                    onSubmit={this.onSubmit}
                    onClose={() => this.onClose(showConfirmationPrompt)}
                    errors={this.state.errors}
                    onClearErrors={this.onClearErrors}
                    error={validationMessage}
                    readOnly={!!saving || details.isDeleted}
                    showNav={showNav}
                    onNavBack={this.props.onNavBack}
                    onNavForward={this.props.onNavForward}
                    saveButtonText="Save Timesheet"
                >
                    <FormBlocker
                        showProgress
                        show={saving}
                    />
                    <FormBlocker
                        showProgress={false}
                        show={showContactsForm || customFormOpened}
                    />
                    <FlexRow>
                        <FormErrorResponseDisplay
                            onClear={this.clearSaveErrors}
                            response={errorResponse}
                        />
                    </FlexRow>
                    <UncontrolledCollapseUnderlineHeader
                        headerText={jobDetailsHeader}
                    >
                        <JobDetails
                            isSelfDispatching={details.isSelfDispatching}
                            isNonFlagging={details.isNonFlagging}
                            customerName={details?.customerName}
                            dispatchOfficeName={details?.dispatchOfficeName}
                            subcontractorName={details?.subcontractorName}
                            customerContactsOptions={customerContactsOptions}
                            enableForemanEditing={isTimesheetApprover}
                            foremanId={details?.foremanId}
                            jobId={details?.jobId}
                            projectId={details?.projectId}
                            initialForemanId={details?.initialForemanId}
                            foremanName={details?.foremanName}
                            foremanPhone={details?.foremanPhone}
                            foremanChanged={details?.foremanChanged} // when there are system log changes
                            foremanChanges={details?.foremanChanges}
                            onEditForeman={() => this.setState({ showContactsForm: true })}
                            onForemanChanged={this.onForemanChanged}
                            enableRequestorEditing={isTimesheetApprover}
                            requestorId={details?.requestorId}
                            initialRequestorId={details?.initialRequestorId}
                            requestorName={details?.requestorName}
                            requestorPhone={details?.requestorPhone}
                            requestorChanged={details?.requestorChanged}
                            requestorChanges={details?.requestorChanges}
                            contractWorkType={details?.contractWorkType}
                            contractSchedule={details?.contractSchedule}
                            isDesignatedMakeupJob={details?.isDesignatedMakeupJob}
                            canDesignateMakeupJob={details?.canDesignateMakeupJob}
                            handleDesignatedMakeupJobChange={this.handleDesignatedMakeupJobChange}
                            onEditRequestor={() => this.setState({ showContactsForm: true })}
                            onRequestorChanged={this.onRequestorChanged}
                            onEditContact={this.onEditContact}
                            onContactSavedComplete={this.onContactSavedComplete}
                            onOrganizationContactSaved={
                                this.onOrganizationContactSaved
                            }
                            jobNotes={details?.jobNotes}
                            jobNumber={details?.jobNumber}
                            jobCity={details?.jobCity}
                            jobStartTime={jobStartTime}
                            jobStartTimeRaw={details?.jobStartTimeRaw}
                            lastRejectionNote={details?.lastRejectionNote}
                            onWorkOrderEditClicked={
                                this.onWorkOrderNumberEditClicked
                            }
                            timesheetNumber={details?.timesheetNumber}
                            timesheetStatus={details?.timesheetStatus}
                            timesheetStatusId={details?.timesheetStatusId}
                            statusRejectionNote={details?.statusRejectionNote}
                            workOrderNumber={details?.workOrderNumber}
                            initialWorkOrderNumber={
                                details?.initialWorkOrderNumber
                            }
                            workOrderNumberChanged={
                                details?.workOrderNumberChanged
                            }
                            workOrderNumberChanges={
                                details?.workOrderNumberChanges
                            }
                            workRequest={details?.workRequest}
                            workRequestChanged={
                                details?.workRequestChanged
                            }
                            initialWorkRequest={
                                details?.initialWorkRequest
                            }
                            workRequestRequired={details?.workRequestRequired}
                            purchaseOrderNumber={details?.purchaseOrderNumber}
                            initialPurchaseOrderNumber={
                                details?.initialPurchaseOrderNumber
                            }
                            purchaseOrderNumberChanged={
                                details?.purchaseOrderNumberChanged
                            }
                            purchaseOrderNumberRequired={details.purchaseOrderNumberRequired}
                            disabledProperties={details?.disabledProperies}
                            timesheetId={details?.timesheetId}
                            enableWorkOrderNumberEditing // {!timesheetApproved}
                            workOrderType={details?.contractWorkOrderType}
                            onWorkOrderChanged={this.onWorkOrderChanged}
                            onWorkRequestChanged={this.onWorkRequestChanged}
                            onPOChanged={this.onPOChanged}
                            onJobCityChanged={this.onJobCityChanged}
                            onRefresh={this.onRefresh}
                            permissions={this.context?.permissions}
                            isAdmin={this.context?.user?.isAdmin}
                            isEmergency={details?.isEmergency}
                            emergencyEmployee={details?.emergencyEmployee}
                            billingEmergencyEmployee={
                                details?.billingEmergencyEmployee
                            }
                            isBillingEmergency={details?.isBillingEmergency}
                            payrollEmergencyEmployee={
                                details?.payrollEmergencyEmployee
                            }
                            isPayrollEmergency={details?.isPayrollEmergency}
                            tenantSettings={tenantSettings}
                            isTimesheetForm={isTimesheetForm}
                            isTimesheet={isTimesheet}
                            isBilling={isBilling}
                            isPayroll={isPayroll}
                            billingStatusId={details?.billingStatusId}
                            payrollStatusId={details?.payrollStatusId}
                            isClientCancelled={details?.isClientCancelled}
                            isJobCancelled={details?.isJobCancelled}
                            isSchedulingErrorCancelled={details?.isSchedulingErrorCancelled}
                            jobName={details?.jobName}
                            enableJobTypeEditing={isTimesheetApprover}
                            jobTypeId={details?.jobTypeId}
                            jobTypeOptions={jobTypeOptions}
                            onJobTypeChanged={this.onJobTypeChanged}
                            initialJobTypeId={details?.initialJobTypeId}
                            jobTypeChanged={details?.jobTypeChanged}
                            jobTimeZone={details?.timeZone}
                            cancelledReasonType={details?.jobCancelledReasonType}
                            showCustomForms={details?.showJobCustomForms}
                            onCustomFormToggle={this.onCustomFormToggle}
                            isEmergencyJob={details?.isEmergencyJob}
                            isExpandedCancellationReasonRequired={details?.expandedCancellationReasonRequired}
                        />
                        {!details?.isSelfDispatching
                            && this.renderLocationDetails(
                                details,
                                timesheetApproved,
                                isTimesheetApprover,
                                countyCaptureEnabled,
                                usCounties,
                            )}
                        {preJobFormSubmission && (
                            <div className="locationDetails">
                                <FormGroup className="mb-2">
                                    <FormLabel
                                        htmlFor="PreJobForm"
                                        label="Pre-Job Form"
                                        className="bottom-border"
                                    />
                                    <FlexStartRow>
                                        <Button
                                            color="link"
                                            size="sm"
                                            className="site-link pl-3"
                                            title="Click to view Pre-Job form."
                                            onClick={
                                                this.toggleJotFormModal
                                            }
                                        >
                                            <i className="fa fa-eye fa-lg mr-1" />
                                            View Pre-Job Form
                                        </Button>
                                    </FlexStartRow>
                                </FormGroup>
                            </div>
                        )}

                    </UncontrolledCollapseUnderlineHeader>

                    <UncontrolledCollapseUnderlineHeader
                        className="timesheet-details-collapse"
                        headerText="Timesheet Details"
                    >
                        <div className="details table-responsive">
                            <table className="table table-sm table-bordered table-striped timesheet-details-table">
                                <thead className="text-muted">
                                    <tr>
                                        {!!tenantSettings.timesheetAdjustmentsEnabled && (
                                            <th className="isadjusted" />
                                        )}
                                        <th className="detailscharge">
                                            Charge
                                        </th>
                                        <th className="detailsname">Name</th>
                                        <th className="detailsnote">Notes</th>
                                        <th className="detailsstart">Start</th>
                                        <th className="detailsend">End</th>
                                        <th className="detailsqty">Quantity</th>
                                        {!!isBilling === true && (
                                            <th className="detailsqty">
                                                Bill Qty
                                            </th>
                                        )}
                                        <th className="detailsunits">Units</th>
                                        <th className="detailsbillable">
                                            Billable
                                        </th>
                                        <th className="detailsbreak">Break</th>
                                        {!!tenantSettings.timesheetAdjustmentsEnabled && (
                                            <Can
                                                do="accept"
                                                on="timesheet"
                                            >
                                                <th className="detailsdelete" />
                                            </Can>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {!(details.timesheetDetails ?? []).length ? (
                                        <tr>
                                            <td
                                                colSpan="5"
                                                className="text-center"
                                            >
                                                No details found.
                                            </td>
                                        </tr>
                                    ) : (
                                        this.groupTimesheetDetails()
                                            .filter((x) => x.show)
                                            .map((detail) => {
                                                const chargeExceedsThreshold = detail.unitsName
                                                    === 'Hours'
                                                    && !detail.isBreak
                                                    && detail.hoursExceededThreshold
                                                    > 0
                                                    && parseFloat(
                                                        detail.quantity,
                                                    )
                                                    >= detail.hoursExceededThreshold;

                                                const breakExceedsThreshold = detail.unitsName
                                                    === 'Hours'
                                                    && detail.isBreak
                                                    && detail.breakHoursExceededThreshold
                                                    > 0
                                                    && Math.abs(
                                                        parseFloat(
                                                            detail.quantity,
                                                        ),
                                                    )
                                                    >= detail.breakHoursExceededThreshold;

                                                return (
                                                    <tr
                                                        key={detail.id}
                                                        className={cls('timesheet-details-table-row', {
                                                            strikeout: detail.removed,
                                                            overlap: !!detail.overlap && !detail.removed,
                                                        })}
                                                    >
                                                        {!!tenantSettings.timesheetAdjustmentsEnabled && (
                                                            <td className="isadjusted">
                                                                {!!detail.isAdjustment && (
                                                                    <Badge className="p-1">
                                                                        ADJ
                                                                    </Badge>
                                                                )}
                                                            </td>
                                                        )}
                                                        <td className="detailscharge">
                                                            <div className="d-inline-flex align-items-center">
                                                                <span>{detail.chargeTypeName}</span>
                                                                {!!detail.isSurcharge && (
                                                                    <Badge className="p-1 ml-1">
                                                                        Surcharge
                                                                    </Badge>
                                                                )}
                                                            </div>

                                                            {detail.estimatedDriveTime > 0 && (
                                                                <FlexStartRow>
                                                                    <span className="text-muted text-small">Estimated travel time: {formatMinutes(detail.estimatedDriveTime)}</span>
                                                                </FlexStartRow>
                                                            )}

                                                            {detail.isRental && !!detail.rentalDate && (
                                                                <FlexStartRow className="flex-nowrap">
                                                                    <span className="text-muted text-small text-nowrap">
                                                                        {detail.isRentalPickup ? 'Rental End: ' : 'Rental Start: '}
                                                                        {detail.rentalUnitTypeId === RentalUnitTypes.Hourly
                                                                            ? detail.rentalDate
                                                                            : moment(detail.rentalDate, 'MMM D, YYYY h:mm A').format('MMM D, YYYY')}
                                                                    </span>
                                                                    {!(this.state.details?.invoiceId > 0) && !detail.rentalDateInvoiced && (
                                                                        <IconButton
                                                                            size="sm"
                                                                            className="site-link p-0 ml-2"
                                                                            title={`Edit ${detail.isRentalPickup ? 'rental end' : 'rental start'} date`}
                                                                            onClick={() => this.editRentalDate(detail)}
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={faPen}
                                                                                size="sm"
                                                                            />
                                                                        </IconButton>
                                                                    )}
                                                                </FlexStartRow>
                                                            )}
                                                        </td>
                                                        <td className="detailsname">
                                                            <FlexBetweenRow>
                                                                <span className={detail.onBoarding ? 'text-danger' : ''}>
                                                                    {detail.resourceName || detail.workflowNotes}

                                                                    {this.state.details.dispatchCompanyId !== detail.resourceDispatchCompanyId && Boolean(detail.resourceDispatchCompanyName) && (
                                                                        <Badge className="my-0 ml-1 cross-dispatch">
                                                                            {detail.resourceDispatchCompanyName}
                                                                        </Badge>
                                                                    )}
                                                                </span>
                                                                {Boolean(detail.overlap) && (
                                                                    <>
                                                                        <FontAwesomeIcon
                                                                            id={`overlap-${detail.id}`}
                                                                            icon={faExclamationTriangle}
                                                                            className="text-warning"
                                                                        />

                                                                        <UncontrolledPopover
                                                                            placement="left"
                                                                            target={`overlap-${detail.id}`}
                                                                            trigger="click focus hover"
                                                                            delay={{
                                                                                show: 0,
                                                                                hide: 300,
                                                                            }}
                                                                        >
                                                                            <PopoverHeader
                                                                                className="white"
                                                                            >
                                                                                <FontAwesomeIcon
                                                                                    icon={faExclamationTriangle}
                                                                                    className="text-warning mr-2"
                                                                                />

                                                                                Potential Overlap Detected
                                                                            </PopoverHeader>

                                                                            <PopoverBody>
                                                                                {this.state.id === detail.overlap.timesheetId ? (
                                                                                    <>
                                                                                        <p>This detail may overlap with another detail on this timesheet.</p>

                                                                                        <List
                                                                                            type="unstyled"
                                                                                            className="mb-0"
                                                                                        >
                                                                                            <li>Charge Type: {detail.overlap.chargeTypeName}</li>
                                                                                            <li>
                                                                                                Employee:
                                                                                                {' '}
                                                                                                <strong>{detail.overlap.resourceName}</strong>
                                                                                                <Link
                                                                                                    to={`${AppNavPaths.Employee}/${detail.overlap.employeeId}`}
                                                                                                    target="_blank"
                                                                                                    className="ml-1"
                                                                                                >
                                                                                                    <FontAwesomeIcon
                                                                                                        icon={faArrowUpRightFromSquare}
                                                                                                        size="sm"
                                                                                                    />
                                                                                                </Link>
                                                                                            </li>
                                                                                        </List>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <p>This detail may overlap with a detail on a different timesheet.</p>

                                                                                        <List
                                                                                            type="unstyled"
                                                                                            className="mb-0"
                                                                                        >
                                                                                            <li>
                                                                                                Charge Type:
                                                                                                <strong>{detail.overlap.chargeTypeName}</strong>
                                                                                            </li>
                                                                                            <li>
                                                                                                Employee:
                                                                                                {' '}
                                                                                                <strong>{detail.overlap.resourceName}</strong>
                                                                                                <Link
                                                                                                    to={`${AppNavPaths.Employee}/${detail.overlap.employeeId}`}
                                                                                                    target="_blank"
                                                                                                    className="ml-1"
                                                                                                >
                                                                                                    <FontAwesomeIcon
                                                                                                        icon={faArrowUpRightFromSquare}
                                                                                                        size="sm"
                                                                                                    />
                                                                                                </Link>
                                                                                            </li>

                                                                                            <li>
                                                                                                Timesheet:
                                                                                                {' '}
                                                                                                <strong>{detail.overlap.timesheetNumber}</strong>
                                                                                                <Link
                                                                                                    to={`${AppNavPaths.TimesheetManagement}?t=${detail.overlap.timesheetId}`}
                                                                                                    target="_blank"
                                                                                                    className="ml-1"
                                                                                                >
                                                                                                    <FontAwesomeIcon
                                                                                                        icon={faArrowUpRightFromSquare}
                                                                                                        size="sm"
                                                                                                    />
                                                                                                </Link>
                                                                                            </li>
                                                                                        </List>
                                                                                    </>
                                                                                )}
                                                                            </PopoverBody>
                                                                        </UncontrolledPopover>
                                                                    </>
                                                                )}
                                                            </FlexBetweenRow>
                                                        </td>
                                                        <td className="detailsnote">
                                                            {!!detail.notes && (
                                                                <div className="userNote">
                                                                    <div className="noteContent">
                                                                        {
                                                                            detail.notes
                                                                        }
                                                                    </div>
                                                                    <div className="noteContentByLine">
                                                                        {detail.isAdjustment || !(detail.modifiedBy && moment(detail.modifiedOn).isAfter(moment(detail.createdOn)))
                                                                            ? `${detail.createdBy}, ${moment(detail.createdOn).fromNow()}`
                                                                            : `${detail.modifiedBy}, ${moment(detail.modifiedOn).fromNow()}`}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="detailsstart">
                                                            {detail.start}
                                                        </td>
                                                        <td className="detailsend">
                                                            {detail.end}
                                                        </td>
                                                        <td className="detailsqty">
                                                            {chargeExceedsThreshold && (
                                                                <i className="fa fa-exclamation-triangle text-warning mr-1" />
                                                            )}
                                                            {breakExceedsThreshold && (
                                                                <i className="fa fa-exclamation-triangle text-danger mr-1" />
                                                            )}
                                                            <span
                                                                className={cls({
                                                                    'text-danger': !!detail.isBreak,
                                                                    'text-warning font-weight-bold': chargeExceedsThreshold,
                                                                    'text-danger font-weight-bold': breakExceedsThreshold,
                                                                })}
                                                            >
                                                                {parseFloat(
                                                                    detail.quantity,
                                                                ).toFixed(2)}
                                                            </span>
                                                        </td>

                                                        {!!isBilling
                                                            === true && (
                                                                <td className="detailsqty">
                                                                    {!!detail.billingQuantity
                                                                        && parseFloat(
                                                                            detail.billingQuantity,
                                                                        ).toFixed(
                                                                            2,
                                                                        )}
                                                                </td>
                                                            )}
                                                        <td className="detailsunits">
                                                            {detail.payrollChargeTypeId === PayrollChargeTypes.PerDiem ? 'Per Diem' : detail.unitsName}
                                                        </td>
                                                        <td className="detailsbillable">
                                                            {detail.billable}
                                                        </td>
                                                        <td className="detailsbreak">
                                                            {Boolean(detail.isBreak) && (
                                                                <>
                                                                    {detail.isPaidBreak
                                                                        ? 'Paid'
                                                                        : 'Unpaid'}
                                                                </>
                                                            )}

                                                            {Boolean(detail.isMissedBreak) && 'Missed'}
                                                        </td>
                                                        {!!tenantSettings.timesheetAdjustmentsEnabled && (
                                                            <Can
                                                                do="accept"
                                                                on="timesheet"
                                                            >
                                                                <td className="detailsdelete">
                                                                    {!detail.isAdjustment
                                                                        && !nonAdjustableTimesheetStatuses.includes(details.timesheetStatusId) && (
                                                                            <FontAwesomeIcon
                                                                                className={`details-remove-button text-danger mt-1 cursor-pointer${detail.removed
                                                                                    ? ' removed'
                                                                                    : ''
                                                                                    }`}
                                                                                title="Strike this charge"
                                                                                onClick={() => this.removeCharge(
                                                                                    detail.resources,
                                                                                )}
                                                                                icon={
                                                                                    faTrash
                                                                                }
                                                                            />
                                                                        )}
                                                                    {!!detail.isAdjustment
                                                                        && !nonAdjustableTimesheetStatuses.includes(details.timesheetStatusId) && (
                                                                            <FontAwesomeIcon
                                                                                className={`details-remove-adjustment-button text-danger mt-1 cursor-pointer${detail.removed
                                                                                    ? ' removed'
                                                                                    : ''
                                                                                    }`}
                                                                                title="Remove this adjustment"
                                                                                onClick={() => this.removeCharge(
                                                                                    detail.resources,
                                                                                )}
                                                                                icon={
                                                                                    faEraser
                                                                                }
                                                                            />
                                                                        )}
                                                                </td>
                                                            </Can>
                                                        )}
                                                    </tr>
                                                );
                                            })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {Boolean(hasTimesheetAuditLogs) && (
                            <p>
                                <button
                                    type="button"
                                    className="text-info"
                                    onClick={() => {
                                        if (this.state.details.timesheetId) {
                                            this.getTimesheetEquipmentLogs();
                                        }
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                    }}
                                >
                                    {timesheetEquipmentLoading ? <Spinner /> : (
                                        <FontAwesomeIcon
                                            style={{
                                                marginRight: 10,
                                            }}
                                            title="View Timesheet Equipment Logs"
                                            icon={
                                                faEye
                                            }
                                        />
                                    )}

                                    View Timesheet Equipment Logs
                                </button>
                            </p>
                        )}

                        <Alert
                            isOpen={this.state.showTimesheetEquipmentLog}
                            title="Timesheet Equipment Log"
                            toggle={() => {
                                this.setState({
                                    showTimesheetEquipmentLog: false,
                                });
                            }}
                        >
                            <div className="equipment-audit table-responsive">
                                {this.state.timesheetEquipmentLoading ? (
                                    <div className="confirmationContainer h-100 pt-2 d-flex flex-row align-items-center justify-content-center">
                                        <Spinner tag="div" />
                                    </div>
                                ) : (
                                    <>
                                        <FormCheckbox
                                            id="showOnlyJobAssignedEquipment"
                                            name="showOnlyJobAssignedEquipment"
                                            labelText="Only Show Job-Assigned Equipment"
                                            checked={this.state.showOnlyJobAssignedEquipment}
                                            onChange={(e) => {
                                                this.setState({
                                                    showOnlyJobAssignedEquipment: e.target.checked,
                                                });
                                            }}
                                        />
                                        {this.renderEquipmentLogTable()}
                                    </>
                                )}
                            </div>
                        </Alert>


                        {!!tenantSettings.timesheetAdjustmentsEnabled
                            && !!(adjustments ?? []).length && (
                                <div className="details">
                                    <small className="text-warning font-weight-bold">
                                        ADD ADJUSTMENTS
                                    </small>
                                    <table className="table table-sm table-bordered table-striped adjustment-table">
                                        <thead className="text-muted">
                                            <tr>
                                                <th className="detailscharge">
                                                    Charge
                                                </th>
                                                <th className="detailsname">
                                                    Name
                                                </th>
                                                <th className="detailsnote">
                                                    Notes
                                                </th>
                                                <th className="detailsstart">
                                                    Start
                                                </th>
                                                <th className="detailsend">
                                                    End
                                                </th>
                                                <th className="detailsqty">
                                                    Quantity
                                                </th>
                                                <th className="detailsunits">
                                                    Units
                                                </th>
                                                <th className="detailsbillable">
                                                    Billable
                                                </th>
                                                <th className="detailsbreak">
                                                    Break
                                                </th>
                                                <Can
                                                    do="accept"
                                                    on="timesheet"
                                                >
                                                    <th className="detailsdelete" />
                                                </Can>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!(adjustments ?? []).length ? (
                                                <tr>
                                                    <td
                                                        colSpan="8"
                                                        className="text-center"
                                                    >
                                                        No adjustments found.
                                                    </td>
                                                </tr>
                                            ) : (
                                                adjustments
                                                    .filter(
                                                        (x) => x.show == true,
                                                    )
                                                    .map((d, i) => {
                                                        // Filter the staff by eligible app groups on the selected charge type.
                                                        const charge_type_user_groups_ids = (
                                                            (
                                                                d.chargeTypeObj
                                                                ?? {}
                                                            )
                                                                .applicationGroup
                                                            ?? {}
                                                        )
                                                            .userGroups
                                                            ?? [];

                                                        const filtered_staff = this.getStaffForCompany(d.dispatchCompanyId).filter(
                                                            (x) => !!(
                                                                x.groupIds.filter(
                                                                    (
                                                                        value,
                                                                    ) => charge_type_user_groups_ids.includes(
                                                                        value,
                                                                    ),
                                                                ) ?? []
                                                            ).length,
                                                        );

                                                        // Filter the equipment by eligible types on the selected charge type.
                                                        const charge_type_eq_types = (
                                                            d.chargeTypeObj
                                                            ?? {}
                                                        ).equipmentTypes
                                                            ?? [];
                                                        const charge_type_eq_types_ids = charge_type_eq_types.map(
                                                            (x) => x.id,
                                                        );
                                                        const filtered_equipment = this.getEquipmentForCompany(d.dispatchCompanyId).filter(
                                                            (x) => charge_type_eq_types_ids.includes(
                                                                x.equipmentTypeId,
                                                            ),
                                                        );

                                                        let isSurcharge = false;
                                                        let chargeTypeDefaultValue = '';
                                                        if (
                                                            d.chargeTypeObj
                                                            && d.chargeTypeObj
                                                                .chargeTypeConfigurations
                                                            && d.chargeTypeObj
                                                                .chargeTypeConfigurations
                                                                .length > 0
                                                        ) {
                                                            const [chargeTypeConfiguration] = d.chargeTypeObj.chargeTypeConfigurations;
                                                            isSurcharge = chargeTypeConfiguration.isSurcharge;

                                                            if (
                                                                tenantSettings.allowDefaultChargeTypeValues
                                                                && chargeTypeConfiguration.defaultValue
                                                            ) {
                                                                chargeTypeDefaultValue = chargeTypeConfiguration.defaultValue;
                                                            }
                                                        }

                                                        const numAFADsInSet = d?.chargeTypeObj?.chargeTypeConfigurations[0]?.numAFADsInSet || 1;

                                                        if (d.adjustmentType) {
                                                            return (
                                                                <tr
                                                                    key={d.id}
                                                                    className="adjustments-details position-relative"
                                                                >
                                                                    <td className="detailscharge">
                                                                        <FormBlocker
                                                                            show={adjustmentLoading === i}
                                                                            showProgress
                                                                        />
                                                                        <ValidatedSelect
                                                                            styles={
                                                                                CompactSelectStyles
                                                                            }
                                                                            minMenuHeight={
                                                                                100
                                                                            }
                                                                            maxMenuHeight={
                                                                                200
                                                                            }
                                                                            name="adjustmentChargeTypeDropdown"
                                                                            required
                                                                            hideClear
                                                                            options={
                                                                                chargeTypes
                                                                            }
                                                                            getOptionLabel={(
                                                                                option,
                                                                            ) => option.description}
                                                                            getOptionValue={(
                                                                                option,
                                                                            ) => option.id}
                                                                            value={
                                                                                (
                                                                                    chargeTypes
                                                                                    ?? []
                                                                                ).find(
                                                                                    (
                                                                                        x,
                                                                                    ) => x.id
                                                                                        === d.chargeTypeId,
                                                                                )
                                                                                ?? ''
                                                                            }
                                                                            onChange={(
                                                                                selection,
                                                                            ) => {
                                                                                const {
                                                                                    details,
                                                                                } = {
                                                                                    ...this
                                                                                        .state,
                                                                                };
                                                                                // when switching charge types, clear any previously selected values.
                                                                                const adj = new TimesheetDetailsAdjustment();
                                                                                adj.chargeTypeId = selection.id;
                                                                                adj.chargeTypeObj = selection;
                                                                                adj.dispatchCompanyId = details.dispatchCompanyId;

                                                                                // for quantity, either the config's default value or a safe fallback of 0
                                                                                if (
                                                                                    adj.chargeTypeObj
                                                                                    && adj
                                                                                        .chargeTypeObj
                                                                                        .chargeTypeConfigurations
                                                                                    && adj
                                                                                        .chargeTypeObj
                                                                                        .chargeTypeConfigurations
                                                                                        .length
                                                                                ) {
                                                                                    const [chargeTypeConfiguration] = adj
                                                                                        .chargeTypeObj
                                                                                        .chargeTypeConfigurations;

                                                                                    adj.quantity = chargeTypeConfiguration.defaultValue;
                                                                                } else {
                                                                                    adj.quantity = 0;
                                                                                }

                                                                                adjustments[
                                                                                    i
                                                                                ] = adj;
                                                                                this.setState(
                                                                                    {
                                                                                        details,
                                                                                    },
                                                                                );
                                                                            }}
                                                                            error="Charge type is required."
                                                                        />
                                                                    </td>
                                                                    <td className="detailsname">
                                                                        {!!d.chargeTypeObj && d.chargeTypeObj.resourceTypeId === ChargeTypeResourceTypes.Employee && (
                                                                            <>
                                                                                {canCrossDispatchStaff && (
                                                                                    <ValidatedSelect
                                                                                        styles={
                                                                                            CompactSelectStyles
                                                                                        }
                                                                                        minMenuHeight={
                                                                                            100
                                                                                        }
                                                                                        maxMenuHeight={
                                                                                            200
                                                                                        }
                                                                                        name="dispatchCompanyDropdown"
                                                                                        required
                                                                                        hideClear
                                                                                        error="Dispatch is required."
                                                                                        options={dispatchCompanies}
                                                                                        getOptionLabel={(option) => option.label}
                                                                                        getOptionValue={(option) => option.value}
                                                                                        value={
                                                                                            dispatchCompanies.find((x) =>
                                                                                                x.value === d.dispatchCompanyId,
                                                                                            ) ?? ''
                                                                                        }
                                                                                        onChange={(
                                                                                            selection,
                                                                                        ) => this.onAdjustmentDispatchCompanyChanged(i, selection, true)}
                                                                                    />
                                                                                )}

                                                                                {filtered_staff.length > 0 && (
                                                                                    <ValidatedSelect
                                                                                        styles={
                                                                                            CompactSelectStyles
                                                                                        }
                                                                                        minMenuHeight={
                                                                                            100
                                                                                        }
                                                                                        maxMenuHeight={
                                                                                            200
                                                                                        }
                                                                                        name="staffDropdown"
                                                                                        required
                                                                                        hideClear
                                                                                        options={
                                                                                            filtered_staff
                                                                                        }
                                                                                        getOptionLabel={(
                                                                                            option,
                                                                                        ) => option.name}
                                                                                        getOptionValue={(
                                                                                            option,
                                                                                        ) => option.id}
                                                                                        value={
                                                                                            filtered_staff.find(
                                                                                                (
                                                                                                    x,
                                                                                                ) => x.id
                                                                                                    === d.resourceId,
                                                                                            )
                                                                                            ?? ''
                                                                                        }
                                                                                        onChange={(
                                                                                            selection,
                                                                                        ) => {
                                                                                            const {
                                                                                                adjustments,
                                                                                            } = {
                                                                                                ...this
                                                                                                    .state,
                                                                                            };
                                                                                            adjustments[
                                                                                                i
                                                                                            ].resourceId = selection.id;
                                                                                            adjustments[
                                                                                                i
                                                                                            ].resourceName = selection.name;
                                                                                            this.setState(
                                                                                                {
                                                                                                    adjustments,
                                                                                                },
                                                                                            );
                                                                                        }}
                                                                                        error="Employee is required."
                                                                                    />
                                                                                )}
                                                                            </>
                                                                        )}

                                                                        {d.chargeTypeObj
                                                                            && (
                                                                                d.chargeTypeObj.resourceTypeId === ChargeTypeResourceTypes.Equipment
                                                                                || d.chargeTypeObj.resourceTypeId === ChargeTypeResourceTypes.Rental
                                                                            ) && (
                                                                                <>
                                                                                    {canCrossDispatchEquipment && (
                                                                                        <ValidatedSelect
                                                                                            styles={
                                                                                                CompactSelectStyles
                                                                                            }
                                                                                            minMenuHeight={
                                                                                                100
                                                                                            }
                                                                                            maxMenuHeight={
                                                                                                200
                                                                                            }
                                                                                            name="dispatchCompanyDropdown"
                                                                                            required
                                                                                            hideClear
                                                                                            error="Dispatch is required."
                                                                                            options={dispatchCompanies}
                                                                                            getOptionLabel={(option) => option.label}
                                                                                            getOptionValue={(option) => option.value}
                                                                                            value={
                                                                                                dispatchCompanies.find((x) =>
                                                                                                    x.value === d.dispatchCompanyId,
                                                                                                ) ?? ''
                                                                                            }
                                                                                            onChange={(
                                                                                                selection,
                                                                                            ) => this.onAdjustmentDispatchCompanyChanged(i, selection, false)}
                                                                                        />
                                                                                    )}

                                                                                    {filtered_equipment.length > 0 && Array.from(Array(numAFADsInSet).keys()).map((index) => {
                                                                                        const resource = d.resources && d.resources[index];

                                                                                        const adjusted = filtered_equipment.map((item) => {
                                                                                            const isVisible = !d.resources
                                                                                                || !d.resources.length
                                                                                                || !d.resources.find((r) => r.id === item.id)
                                                                                                || (d.resources.find((r) => r.id === item.id) && item.id === resource?.id);

                                                                                            return {
                                                                                                ...item,
                                                                                                isDisabled: !isVisible,
                                                                                            };
                                                                                        });

                                                                                        return (
                                                                                            <ValidatedSelect
                                                                                                styles={
                                                                                                    CompactSelectStyles
                                                                                                }
                                                                                                minMenuHeight={
                                                                                                    100
                                                                                                }
                                                                                                maxMenuHeight={
                                                                                                    200
                                                                                                }
                                                                                                name={`equipmentDropdown[${index}]`}
                                                                                                required={numAFADsInSet === 1}
                                                                                                key={`equipmentDropdown[${index}]`}
                                                                                                hideClear
                                                                                                options={
                                                                                                    adjusted
                                                                                                }
                                                                                                getOptionLabel={(
                                                                                                    option,
                                                                                                ) => option.description}
                                                                                                getOptionValue={(
                                                                                                    option,
                                                                                                ) => option.id}
                                                                                                value={
                                                                                                    (
                                                                                                        filtered_equipment
                                                                                                        ?? []
                                                                                                    ).find(
                                                                                                        (
                                                                                                            x,
                                                                                                        ) => x.id
                                                                                                            === resource?.id,
                                                                                                    )
                                                                                                    ?? ''
                                                                                                }
                                                                                                onChange={(
                                                                                                    selection,
                                                                                                ) => {
                                                                                                    const {
                                                                                                        adjustments,
                                                                                                    } = {
                                                                                                        ...this
                                                                                                            .state,
                                                                                                    };

                                                                                                    adjustments[
                                                                                                        i
                                                                                                    ].resourceId = selection.id;

                                                                                                    adjustments[
                                                                                                        i
                                                                                                    ].resourceName = selection.description;

                                                                                                    adjustments[i].resources[index] = selection;

                                                                                                    this.setState(
                                                                                                        {
                                                                                                            adjustments,
                                                                                                        },
                                                                                                    );
                                                                                                }}
                                                                                                error="Equipment is required."
                                                                                            />
                                                                                        );
                                                                                    })}
                                                                                </>
                                                                            )}

                                                                        {d.chargeTypeObj?.resourceTypeId === ChargeTypeResourceTypes.Rental && (
                                                                            <FormCheckbox
                                                                                small
                                                                                className="mt-1"
                                                                                id={`adjustment-${i}-isRentalPickup`}
                                                                                name={`adjustment-${i}-isRentalPickup`}
                                                                                checked={d.isRentalPickup ?? false}
                                                                                onChange={(e) => {
                                                                                    const { checked } = e.target;
                                                                                    const adjustments = [...this.state.adjustments];
                                                                                    adjustments[i] = { ...adjustments[i], isRentalPickup: checked };
                                                                                    this.setState({ adjustments });
                                                                                }}
                                                                                labelText="This is a pickup"
                                                                            />

                                                                        )}
                                                                    </td>
                                                                    <td className="detailsnotes">
                                                                        <Textarea
                                                                            disabled={!d.chargeTypeObj}
                                                                            required
                                                                            type="textarea"
                                                                            name="notes"
                                                                            value={d.notes ?? ''}
                                                                            onChange={(evt) => {
                                                                                const { value } = evt.target;
                                                                                const { adjustments } = { ...this.state };
                                                                                adjustments[i].notes = value;
                                                                                this.setState({ adjustments });
                                                                            }}
                                                                            error="Adjustment notes are required"
                                                                        />
                                                                    </td>
                                                                    <td className="detailsstart">
                                                                        {!isSurcharge
                                                                            && !d.chargeTypeObj?.payrollChargeTypeId
                                                                            && (
                                                                                d.chargeTypeObj?.unitsId === ChargeTypeUnits.Hours
                                                                                || d.chargeTypeObj?.rentalUnitTypeId === RentalUnitTypes.Hourly
                                                                            ) && (
                                                                                <TimePicker
                                                                                    required
                                                                                    name="start"
                                                                                    value={d.start}
                                                                                    increment={
                                                                                        d.chargeTypeObj?.rentalUnitTypeId === RentalUnitTypes.Hourly
                                                                                            ? 0.25
                                                                                            : d.chargeTypeObj.chargeTypeConfigurations[0].increment || 0.25
                                                                                    }
                                                                                    onChange={(
                                                                                        ev,
                                                                                    ) => this.onAdjustmentTimeEntryChanged(
                                                                                        i,
                                                                                        ev,
                                                                                    )}
                                                                                />
                                                                            )}
                                                                    </td>
                                                                    <td className="detailsend">
                                                                        {!isSurcharge
                                                                            && !d.chargeTypeObj?.payrollChargeTypeId
                                                                            && d.chargeTypeObj?.unitsId === ChargeTypeUnits.Hours && (
                                                                                <TimePicker
                                                                                    required
                                                                                    name="end"
                                                                                    value={
                                                                                        d.end
                                                                                    }
                                                                                    increment={
                                                                                        d
                                                                                            .chargeTypeObj
                                                                                            .chargeTypeConfigurations[0]
                                                                                            .increment
                                                                                    }
                                                                                    onChange={(
                                                                                        ev,
                                                                                    ) => this.onAdjustmentTimeEntryChanged(
                                                                                        i,
                                                                                        ev,
                                                                                    )}
                                                                                />
                                                                            )}
                                                                    </td>
                                                                    <td className="detailsqty">
                                                                        {!isSurcharge
                                                                            && !d.chargeTypeObj?.payrollChargeTypeId
                                                                            && d.chargeTypeObj?.unitsId === ChargeTypeUnits.Hours && (
                                                                                <>
                                                                                    {parseFloat(d.quantity ?? 0) > 16.0 && (
                                                                                        <i className="fa fa-exclamation-triangle text-warning mr-1" />
                                                                                    )}

                                                                                    <span
                                                                                        className={cls(
                                                                                            {
                                                                                                'text-warning font-weight-bold':
                                                                                                    parseFloat(
                                                                                                        d?.quantity
                                                                                                        ?? 0,
                                                                                                    )
                                                                                                    > 16.0,
                                                                                            },
                                                                                        )}
                                                                                    >
                                                                                        {d.quantity ?? '-'}
                                                                                    </span>
                                                                                </>
                                                                            )}

                                                                        {(d.chargeTypeObj?.unitsId === ChargeTypeUnits.Flat
                                                                            || isSurcharge
                                                                            || d.chargeTypeObj?.payrollChargeTypeId > 0
                                                                        )
                                                                            && (
                                                                                <NumericInput
                                                                                    className="form-control-sm text-right"
                                                                                    name="quantity"
                                                                                    required
                                                                                    value={d.quantity ?? chargeTypeDefaultValue}
                                                                                    onChange={(event) => {
                                                                                        const val = event.target.value;
                                                                                        const adjustments = [...this.state.adjustments];
                                                                                        adjustments[i] = { ...adjustments[i], quantity: val };
                                                                                        this.setState({ adjustments });
                                                                                    }}
                                                                                    min={0}
                                                                                    max={(numAFADsInSet ?? 1) > 1 ? 1 : undefined}
                                                                                    step={(numAFADsInSet ?? 1) > 1 ? 1 : d.chargeTypeObj?.baseConfig?.increment}
                                                                                    preventNegative
                                                                                />
                                                                            )}

                                                                        {!d.chargeTypeObj && !isSurcharge && (
                                                                            <span>
                                                                                {
                                                                                    ' - '
                                                                                }
                                                                            </span>
                                                                        )}
                                                                    </td>

                                                                    <td className="detailsunits">
                                                                        {d.chargeTypeObj?.payrollChargeTypeId === PayrollChargeTypes.PerDiem
                                                                            ? 'Per Diem'
                                                                            : d.chargeTypeObj?.unitsName ?? '-'}
                                                                    </td>

                                                                    <td className="detailsbillable">
                                                                        {!!d.chargeTypeObj
                                                                            && (d
                                                                                .chargeTypeObj
                                                                                .isBillable
                                                                                ? 'Yes'
                                                                                : 'No')}
                                                                        {!d.chargeTypeObj
                                                                            && '-'}
                                                                    </td>

                                                                    <td className="detailsbreak">
                                                                        {Boolean(d.chargeTypeObj?.isBreak) && (
                                                                            <>
                                                                                {d.chargeTypeObj.isPaidBreak
                                                                                    ? 'Paid'
                                                                                    : 'Unpaid'}
                                                                            </>
                                                                        )}

                                                                        {Boolean(d.chargeTypeObj?.isMissedBreak) && 'Missed'}
                                                                        {!d.chargeTypeObj
                                                                            && '-'}
                                                                    </td>
                                                                    <Can
                                                                        do="accept"
                                                                        on="timesheet"
                                                                    >
                                                                        <td className="detailsdelete">
                                                                            {!nonAdjustableTimesheetStatuses.includes(details.timesheetStatusId) && (
                                                                                <FontAwesomeIcon
                                                                                    className="text-danger mt-1 cursor-pointer"
                                                                                    title="Remove this charge"
                                                                                    onClick={() => {
                                                                                        const {
                                                                                            adjustments,
                                                                                        } = {
                                                                                            ...this
                                                                                                .state,
                                                                                        };
                                                                                        adjustments.splice(
                                                                                            i,
                                                                                            1,
                                                                                        );
                                                                                        this.setState(
                                                                                            {
                                                                                                adjustments,
                                                                                            },
                                                                                        );
                                                                                    }}
                                                                                    icon={
                                                                                        faTrash
                                                                                    }
                                                                                />
                                                                            )}
                                                                        </td>
                                                                    </Can>
                                                                </tr>
                                                            );
                                                        }
                                                        return (
                                                            <tr key={d.id}>
                                                                <td className="detailscharge">
                                                                    {
                                                                        d.chargeTypeName
                                                                    }
                                                                </td>
                                                                <td className="detailsname">
                                                                    {
                                                                        d.resourceName
                                                                    }
                                                                </td>
                                                                <td className="detailsstart" />
                                                                <td className="detailsend" />
                                                                <td className="detailsqty">
                                                                    {parseFloat(
                                                                        d.quantity
                                                                        ?? 0,
                                                                    )
                                                                        > 16.0 && (
                                                                            <i className="fa fa-exclamation-triangle text-warning mr-1" />
                                                                        )}
                                                                    <span
                                                                        className={cls(
                                                                            {
                                                                                'text-warning font-weight-bold':
                                                                                    parseFloat(
                                                                                        d?.quantity
                                                                                        ?? 0,
                                                                                    )
                                                                                    > 16.0,
                                                                            },
                                                                        )}
                                                                    >
                                                                        {d.quantity
                                                                            ?? '-'}
                                                                    </span>
                                                                </td>
                                                                <td className="detailsunits">
                                                                    {
                                                                        d.unitsName
                                                                    }
                                                                </td>
                                                                <td className="detailsbillable">
                                                                    {
                                                                        d.billable
                                                                    }
                                                                </td>
                                                                <td className="detailsbreak">
                                                                    {Boolean(d.isBreak) && (
                                                                        <>
                                                                            {d.isPaidBreak
                                                                                ? 'Paid'
                                                                                : 'Unpaid'}
                                                                        </>
                                                                    )}

                                                                    {Boolean(d.isMissedBreak) && 'Missed'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        {!!tenantSettings.timesheetAdjustmentsEnabled
                            && !nonAdjustableTimesheetStatuses.includes(details.timesheetStatusId)
                            && isTimesheet === true && (
                                <Can
                                    do="accept"
                                    on="timesheet"
                                >
                                    <FlexCenterRow>
                                        <Button
                                            className="mb-2"
                                            size="sm"
                                            type="button"
                                            color="secondary"
                                            onClick={() => {
                                                const { adjustments } = {
                                                    ...this.state,
                                                };
                                                const newRecord = new TimesheetDetailsAdjustment();
                                                adjustments.push(newRecord);
                                                this.setState({
                                                    adjustments,
                                                });
                                            }}
                                        >
                                            <i className="fa fa-pen-square fa-lg mr-2" />
                                            Add Adjustment
                                        </Button>
                                    </FlexCenterRow>
                                </Can>
                            )}
                    </UncontrolledCollapseUnderlineHeader>

                    {!!timesheetInputs.length && (
                        <UncontrolledCollapseUnderlineHeader
                            className="mb-3"
                            headerText="Timesheet Inputs"
                        >
                            {timesheetInputs.map((inp, inpx) => (
                                <table
                                    key={inp.question + inpx}
                                    className="table table-sm table-bordered table-striped"
                                >
                                    <thead>
                                        <tr>
                                            <th colSpan={3}>
                                                <h6
                                                    style={{
                                                        marginBottom: 0,
                                                    }}
                                                >
                                                    {`${inp.question}`}
                                                </h6>
                                            </th>
                                        </tr>
                                    </thead>
                                    <thead className="text-muted">
                                        <tr>
                                            <th>Employee</th>
                                            {inp.hasDetails && <th>Details</th>}
                                            <th>Answer</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(inp.answers ?? []).map(
                                            (ans) => (
                                                <tr
                                                    key={
                                                        ans.employee
                                                        + ans.details
                                                        + ans.value
                                                    }
                                                >
                                                    <td style={{ width: '33%' }}>{`${ans.employee}`}</td>
                                                    {inp.hasDetails && <td style={{ width: '33%' }}>{ans.details}</td>}
                                                    <td style={{ width: '33%' }}>{`${ans.value}`}</td>
                                                </tr>
                                            ),
                                        )}
                                    </tbody>
                                </table>
                            ))}
                        </UncontrolledCollapseUnderlineHeader>
                    )}

                    {details?.timesheetId && (
                        <UncontrolledCollapseUnderlineHeader
                            headerText="Flags"
                        >
                            <TimesheetFlagsSection timesheetId={details.timesheetId} />
                        </UncontrolledCollapseUnderlineHeader>
                    )}

                    {(details.reviewDetails ?? []).some((d) => d.isCrewLead && d.wasProjectCompleted !== null) && (
                        <UncontrolledCollapseUnderlineHeader
                            className="mb-3"
                            headerText="Job Completion"
                        >
                            {details.reviewDetails
                                .filter((d) => d.isCrewLead && d.wasProjectCompleted !== null)
                                .map((d, index) => (
                                    <FormGroup key={index}>
                                        <FlexStartRow>
                                            <FontAwesomeIcon
                                                className={cls('mr-2', d.wasProjectCompleted ? 'text-success' : 'text-danger')}
                                                icon={d.wasProjectCompleted ? faCircleCheck : faTriangleExclamation}
                                            />
                                            <span>
                                                <strong>
                                                    {d.name} ({crewLeadLabel(tenantSettings)})
                                                </strong>
                                                {' — '}
                                                {d.wasProjectCompleted
                                                    ? 'Project was completed on this timesheet.'
                                                    : 'Project was not completed on this timesheet.'}
                                            </span>
                                        </FlexStartRow>
                                    </FormGroup>
                                ))}
                        </UncontrolledCollapseUnderlineHeader>
                    )}

                    <UncontrolledCollapseUnderlineHeader
                        headerText="Signatures"
                    >
                        <table className="table table-sm table-bordered table-striped">
                            <thead className="text-muted">
                                <tr>
                                    <th>Review Type</th>
                                    <th>Signature Type</th>
                                    <th>Name</th>
                                    <th>Notes</th>
                                    <th>Signed On</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {!(details.reviewDetails ?? []).length ? (
                                    <tr>
                                        <td
                                            colSpan="5"
                                            className="text-center"
                                        >
                                            No signatures found.
                                        </td>
                                    </tr>
                                ) : (
                                    details.reviewDetails.map((d) => {
                                        const signedOn = formatOffset(d.signedOnOffset, 'YYYY-MM-DD h:mm:ss A', d.timeZone, d.signedOn);
                                        return (
                                            <tr key={d.id}>
                                                <td>
                                                    {d.reviewType}
                                                    {d.isCrewLead && ` (${crewLeadLabel(tenantSettings)})`}
                                                </td>
                                                <td>{d.signatureType}</td>
                                                <td>{d.name}</td>
                                                <td>{d.notes}</td>
                                                <td>{signedOn}</td>
                                                <td className="timesheet-details-signature-image">
                                                    {
                                                        d.signatureTypeId
                                                        != 3
                                                        && d.signatureTypeId
                                                        != 5 && (
                                                            <SignatureImage
                                                                reviewId={d.id}
                                                                initialSrc={(d ?? {}).signatureData}
                                                                alt="Signature Preview"
                                                                id="sigPreview"
                                                                style={{
                                                                    maxWidth:
                                                                        '100%',
                                                                    height: 'auto',
                                                                    border: '1px solid #ccc',
                                                                }}
                                                            />
                                                        )
                                                    }
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </UncontrolledCollapseUnderlineHeader>

                    {!!tenantSettings.jobUploadsEnabled && (
                        <UncontrolledCollapseUnderlineHeader
                            className="mb-3"
                            headerText="Job Attachments"
                        >
                            <FormGroup className="mt-3">
                                <FileList
                                    jobId={details.jobId}
                                    files={details.jobUploads ?? []}
                                />
                            </FormGroup>
                        </UncontrolledCollapseUnderlineHeader>
                    )}
                    {(!!allowTimesheetAttachments || details?.attachments?.length > 0) && (
                        <UncontrolledCollapseUnderlineHeader
                            className="mb-3"
                            headerText="Timesheet Attachments"
                        >
                            <FormGroup className="mt-3 timesheetAttachmentsSection">
                                <LoadingOverlay show={attachmentsLoading}>
                                    <FileUpload
                                        url={ApiRoutes.fileUpload.createAttachment()}
                                        accept={['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.doc', '.docx', '.xls', '.xlsx']}
                                        files={details?.attachments}
                                        onQueueComplete={this.onUploadComplete}
                                        onDownload={onFileDownload}
                                        onDelete={this.onFileDelete}
                                        type="simple"
                                        buttonText="Add Attachment"
                                        buttonProps={{ selected: true, className: 'mt-2 ml-0 mb-2' }}
                                        buttonIcon={faUpload}
                                        stacked
                                        fileIcon={faFileImage}
                                        disabled={attachmentsLoading || !isTimesheetApprover}
                                    />
                                </LoadingOverlay>
                            </FormGroup>
                        </UncontrolledCollapseUnderlineHeader>
                    )}
                    {!!tenantSettings.enablePaintBatchCodes && !details?.isNonFlagging && (
                        <UncontrolledCollapseUnderlineHeader
                            className="mb-3"
                            headerText="Paint Batch Codes"
                        >
                            <FormGroup className="mt-3">
                                {(details?.paintBatchCodes ?? '').split(',').filter(Boolean).map((code) => (
                                    <div
                                        key={code}
                                        className="mb-1"
                                    >
                                        {code}
                                    </div>
                                ))}
                                {(paintBatchPhotos ?? []).map((file) => (
                                    <div
                                        key={file.id}
                                        className="d-flex align-items-center mb-1"
                                    >
                                        <Button
                                            color="link"
                                            className="site-link p-0 mr-1"
                                            onClick={() => onFileDownload(file)}
                                            style={{ fontSize: '0.875rem' }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faFileImage}
                                                className="mr-1"
                                                size="sm"
                                            />
                                            {file.name}
                                        </Button>
                                    </div>
                                ))}
                            </FormGroup>
                        </UncontrolledCollapseUnderlineHeader>
                    )}
                    {this.shouldShowWorkCard() && (
                        <UncontrolledCollapseUnderlineHeader
                            headerText="Work Cards"
                        >
                            <table className="table table-sm table-bordered table-striped">
                                <thead className="text-muted">
                                    <tr>
                                        <th>Name</th>
                                        <th>Work Card</th>
                                        {this.isWorkCardRequired() && <th>Status</th>}
                                        <th>Replace Work Card</th>
                                        <th>Work Card Last Modified By</th>
                                        <th>Work Card Last Modified On</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!(timesheetWorkCards ?? []).length ? (
                                        <tr>
                                            <td
                                                colSpan={this.isWorkCardRequired() ? '3' : '2'}
                                                className="text-center"
                                            >
                                                {this.isWorkCardRequired()
                                                    ? 'Work cards are required but none have been uploaded.'
                                                    : 'No work cards found.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        timesheetWorkCards.map((d) => {
                                            const modifiedOn = d.workCardModifiedOn
                                                ? formatOffset(d.workCardModifiedOn, 'YYYY-MM-DD h:mm:ss A', d.timeZone, null)
                                                : '';
                                            return (
                                                <tr key={d.empId}>
                                                    <td width="20%">{d.empName}</td>
                                                    <td width="20%">
                                                        {d.workCardUrl ? (
                                                            <>
                                                                <Button
                                                                    id={`viewWorkCard-${d.empId}`}
                                                                    color="link"
                                                                    className="p-0"
                                                                    onClick={() => showWorkCardModal(d)}
                                                                >
                                                                    <img
                                                                        src={d.workCardUrl}
                                                                        alt="Work Card"
                                                                        className="work-card-image"
                                                                    />
                                                                </Button>
                                                                <UncontrolledTooltip
                                                                    placement="top"
                                                                    target={`viewWorkCard-${d.empId}`}
                                                                >
                                                                    Click to view the work card at full size.
                                                                </UncontrolledTooltip>
                                                            </>
                                                        ) : (
                                                            <span className="text-muted">No work card uploaded</span>
                                                        )}
                                                    </td>
                                                    {this.isWorkCardRequired() && (
                                                        <td>
                                                            {d.workCardUrl ? (
                                                                <span className="text-success">
                                                                    <FontAwesomeIcon
                                                                        icon={faCheck}
                                                                        className="mr-1"
                                                                    />
                                                                    Uploaded
                                                                </span>
                                                            ) : (
                                                                <span className="text-danger">
                                                                    <FontAwesomeIcon
                                                                        icon={faTimes}
                                                                        className="mr-1"
                                                                    />
                                                                    Required
                                                                </span>
                                                            )}
                                                        </td>
                                                    )}
                                                    <td>
                                                        <div id={`replaceWorkCard-${d.empId}`}>
                                                            <FileUpload
                                                                id="jobUploader"
                                                                accept={supportedFiles}
                                                                maxNameLength={150}
                                                                onUploadComplete={() => this.handleUploadComplete(d.empId)}
                                                                url={ApiRoutes.timesheetReview.postWorkCard(
                                                                    details?.timesheetId,
                                                                    d.empId,
                                                                )}
                                                                disabled={workCardLocked}
                                                            />
                                                        </div>
                                                        {!workCardLocked && (
                                                            <UncontrolledTooltip
                                                                placement="top"
                                                                target={`replaceWorkCard-${d.empId}`}
                                                            >
                                                                Uploading a file will immediately replace the existing work card.
                                                                No additional save is required.
                                                            </UncontrolledTooltip>
                                                        )}
                                                        {workCardLocked && (
                                                            <UncontrolledTooltip
                                                                placement="top"
                                                                target={`replaceWorkCard-${d.empId}`}
                                                            >
                                                                Work cards cannot be replaced once invoiced.
                                                            </UncontrolledTooltip>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {d.workCardModifiedBy}
                                                    </td>
                                                    <td>
                                                        {modifiedOn}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </UncontrolledCollapseUnderlineHeader>
                    )}
                    <UncontrolledCollapseUnderlineHeader
                        className="mb-3"
                        headerText="Field Notes"
                    >
                        <FormGroup>
                            <FormCheckbox
                                className="font-weight-bold"
                                id="doNotPrintFieldNotes"
                                disabled={!!saving || !isTimesheetApprover}
                                readOnly={!!saving || !isTimesheetApprover}
                                checked={
                                    details?.doNotPrintFieldNotes ?? ''
                                }
                                onChange={(e) => {
                                    const { checked } = e.target;
                                    this.setState(
                                        (state) => (
                                            (details.doNotPrintFieldNotes = checked),
                                            state
                                        ),
                                    );
                                }}
                                labelText="Do Not Print Field Notes"
                            />
                        </FormGroup>
                        <FormGroup>
                            {details.notes ?? 'No notes to display.'}
                        </FormGroup>
                    </UncontrolledCollapseUnderlineHeader>

                    {details.timesheetDeletion && (
                        <UncontrolledCollapseUnderlineHeader
                            className="mb-3"
                            headerText="Deletion Notes"
                        >
                            <FormGroup>
                                {details.timesheetDeletion.note ?? 'No notes to display.'}
                                {details.timesheetDeletion.employeeName && (
                                    <p
                                        style={{ opacity: 0.5 }}
                                    >
                                        Deleted by {details.timesheetDeletion.employeeName} on {details.timesheetDeletion.deletedOn}
                                    </p>
                                )}
                            </FormGroup>
                        </UncontrolledCollapseUnderlineHeader>
                    )}

                    {tenantSettings.enableDriverRideConfirmationFields && (
                        <UncontrolledCollapseUnderlineHeader
                            className="mb-3"
                            headerText="Driver Agreement and Disclaimer"
                        >
                            {isEmpty(details.reviewDetails) ? (
                                <span>No driver agreement or disclaimer details available.</span>
                            ) : (
                                <>
                                    {details.reviewDetails.every((driver) => !driver.hasAgreedToDriverDisclaimer && !driver.hasAgreedToDriveCrew) ? (
                                        <span>No driver agreement or disclaimer details available.</span>
                                    ) : details.reviewDetails.map((driver, index) => {
                                        if (driver.hasAgreedToDriverDisclaimer || driver.hasAgreedToDriveCrew) {
                                            return (
                                                <FormGroup key={index}>
                                                    {/* Driver Name */}
                                                    <FlexStartRow>
                                                        <FontAwesomeIcon
                                                            className="text-success mr-2"
                                                            icon={faUserClock}
                                                        />
                                                        <strong>{driver.name}</strong>
                                                    </FlexStartRow>

                                                    {/* Driver Disclaimer */}
                                                    {driver.hasAgreedToDriverDisclaimer ? (
                                                        <FlexStartRow>
                                                            <FontAwesomeIcon
                                                                className="text-success mr-2"
                                                                icon={faCircleCheck}
                                                            />
                                                            The driver has acknowledged that providing rides is voluntary and not considered hours worked.
                                                        </FlexStartRow>
                                                    ) : (
                                                        <FlexStartRow>
                                                            <FontAwesomeIcon
                                                                icon={faTriangleExclamation}
                                                                className="text-danger mr-2"
                                                            />
                                                            The driver has not acknowledged the disclaimer.
                                                        </FlexStartRow>
                                                    )}

                                                    {/* Driver Agreement */}
                                                    {driver.hasAgreedToDriveCrew ? (
                                                        <FlexStartRow>
                                                            <FontAwesomeIcon
                                                                className="text-success mr-2"
                                                                icon={faCircleCheck}
                                                            />
                                                            The driver has agreed to provide a ride to their crew partner as directed by dispatch.
                                                        </FlexStartRow>
                                                    ) : (
                                                        <FlexStartRow>
                                                            <FontAwesomeIcon
                                                                icon={faTriangleExclamation}
                                                                className="text-warning mr-2"
                                                            />
                                                            The driver did not provide a ride at this time
                                                        </FlexStartRow>
                                                    )}
                                                </FormGroup>
                                            );
                                        }
                                        return null; // Skip rendering if neither disclaimer nor agreement is true
                                    })}
                                </>
                            )}
                        </UncontrolledCollapseUnderlineHeader>
                    )}

                    {!details.isSelfDispatching && (
                        <UncontrolledCollapseUnderlineHeader
                            className="mb-3"
                            headerText="Job Assignment Workflow Notes"
                        >
                            <FormGroup>
                                {(details.workflowNotes ?? []).map((x, i) => (
                                    <FlexStartRow key={i}>{`${x.resourceName} set to ${x.status} status by ${x.modifiedBy} on ${moment.utc(x.modifiedOnUTC).local().format(MomentDateFormat.DateTimeShortAnte)}`}</FlexStartRow>
                                ))}
                                {!(details?.workflowNotes ?? []).length && (
                                    <span>
                                        No job workflow notes to display.
                                    </span>
                                )}
                            </FormGroup>
                        </UncontrolledCollapseUnderlineHeader>
                    )}
                    {!details.isSelfDispatching && (
                        <UncontrolledCollapseUnderlineHeader
                            className="mb-3"
                            headerText="Cancellation Notes"
                        >
                            <FormGroup>
                                {(details.cancellationNotes ?? []).map(
                                    (x, i) => (
                                        <FlexStartRow key={i}>{x}</FlexStartRow>
                                    ),
                                )}
                                {!(details?.cancellationNotes ?? []).length && (
                                    <span>
                                        No cancellation notes to display.
                                    </span>
                                )}
                            </FormGroup>
                        </UncontrolledCollapseUnderlineHeader>
                    )}
                    {!details.isSelfDispatching && (
                        <UncontrolledCollapseUnderlineHeader
                            className="mb-3"
                            headerText="Billable Notes"
                        >
                            <FormGroup>
                                <FormCheckbox
                                    className="font-weight-bold"
                                    id="doNotPrintNotesDisplay"
                                    disabled={!!saving || !isTimesheetApprover}
                                    readOnly={!!saving || !isTimesheetApprover}
                                    checked={
                                        details?.doNotPrintNotesDisplay ?? ''
                                    }
                                    onChange={(e) => {
                                        const { checked } = e.target;
                                        this.setState(
                                            (state) => (
                                                (details.doNotPrintNotesDisplay = checked),
                                                state
                                            ),
                                        );
                                    }}
                                    labelText="Do Not Print Billable Notes"
                                />
                            </FormGroup>
                            <Textarea
                                disabled={!!saving || !isTimesheetApprover}
                                readOnly={!!saving || !isTimesheetApprover}
                                name="notesDisplay"
                                id="notesDisplayTextArea"
                                placeholder={
                                    !details?.doNotPrintNotesDisplay
                                        ? 'This note will display on the printed timesheet.'
                                        : 'This note will not display on the printed timesheet.'
                                }
                                value={details.notesDisplay ?? ''}
                                onChange={(event) => {
                                    const { details } = { ...this.state };
                                    const { value } = event.target;
                                    details.notesDisplay = value;
                                    this.setState({
                                        details: { ...details },
                                    });
                                }}
                                rows="5"
                                maxLength="500"
                            />
                        </UncontrolledCollapseUnderlineHeader>
                    )}
                    <UncontrolledCollapseUnderlineHeader
                        className="mb-3"
                        headerText="Internal Notes"
                    >
                        <div className="mt-4">
                            <Textarea
                                disabled={!!saving || !isTimesheetApprover}
                                readOnly={!!saving || !isTimesheetApprover}
                                name="notesInternal"
                                id="notesInternalTextArea"
                                placeholder="This note will NOT display on the printed time sheet and is for INTERNAL USE only."
                                value={details.notesInternal ?? ''}
                                onChange={(event) => {
                                    const { details } = { ...this.state };
                                    const { value } = event.target;
                                    details.notesInternal = value;
                                    this.setState({
                                        details: { ...details },
                                    });
                                }}
                                rows="5"
                                maxLength="500"
                            />
                        </div>
                    </UncontrolledCollapseUnderlineHeader>

                    {!!showTimesheetStatusHistory && (
                        <UncontrolledCollapseUnderlineHeader
                            className="timesheet-details-collapse"
                            headerText="Timesheet Status History"
                        >
                            <div className="details table-responsive">
                                <table className="table table-sm table-bordered table-striped timesheet-details-table">
                                    <thead className="text-muted">
                                        <tr>
                                            <th>Status Changed To</th>
                                            <th>Changed By</th>
                                            <th>Changed On</th>
                                            <th>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!groups?.length ? (
                                            <tr>
                                                <td
                                                    colSpan="5"
                                                    className="text-center"
                                                >No details found.</td>
                                            </tr>
                                        ) : (
                                            groups.map((g, i) => (
                                                <tr key={`${g.changedOn}-${i}`}>
                                                    <td style={{ width: '25%' }}>
                                                        {g.items.map((it, idx) => (
                                                            <div key={`${it.type}-${it.timesheetStatus}-${idx}`}>
                                                                {`${it.type} \u2192 ${it.timesheetStatus}`}
                                                            </div>
                                                        ))}
                                                    </td>
                                                    <td style={{ width: '20%' }}>
                                                        {Array.from(g.changedBy).join(', ')}
                                                    </td>
                                                    <td style={{ width: '15%' }}>
                                                        {formatOffsetInTimeZone(g.changedOn, 'MM/DD/YYYY, h:mm A', g.timeZone)}
                                                    </td>
                                                    <td style={{ width: '40%' }}>
                                                        {g.notes}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </UncontrolledCollapseUnderlineHeader>
                    )}
                    <FlexCenterRow className="pb-3">
                        {(isTimesheet === true || isBilling === true)
                            && details.canEditChargeTypes && (
                                <Button
                                    type="submit"
                                    className="mr-3"
                                    color="primary"
                                    disabled={!!saving}
                                >
                                    <FontAwesomeIcon
                                        size="lg"
                                        icon={saving ? faCircleNotch : faSave}
                                        className="mr-2"
                                    />
                                    {saving
                                        ? 'Saving, Please Wait...'
                                        : 'Save Timesheet'}
                                </Button>
                            )}
                        {((details ?? {}).timesheetStatusId
                            === TimesheetStatus.Submitted
                            || (details ?? {}).timesheetStatusId
                            === TimesheetStatus.BillingPayrollRejected)
                            && isTimesheet === true
                            && isPayroll == false && (
                                <>
                                    {!isEquipmentOnly && (
                                        <Can
                                            do="reject"
                                            on={details?.isNonFlagging ? 'timesheet' : 'flagging_timesheet'}
                                        >
                                            <Button
                                                className="mr-3"
                                                style={{ marginRight: '10px' }}
                                                color="danger"
                                                onClick={() => this.confirmRejectTimesheet(showConfirmationPrompt, true)}
                                            >
                                                <i className="fa fa-lg fa-times mr-2" />
                                                Reject
                                            </Button>
                                        </Can>
                                    )}
                                    {details.canApproveChargeTypes && (
                                        <Can
                                            do="accept"
                                            on="timesheet"
                                        >
                                            <Button
                                                className="mr-3"
                                                color="success"
                                                onClick={this.approveTimesheetWithValidation}
                                            >
                                                <i className="fa fa-lg fa-check mr-2" />
                                                Approve
                                            </Button>
                                        </Can>
                                    )}
                                </>
                            )}

                        {(details ?? {}).timesheetStatusId != TimesheetStatus.ApprovalPending
                            && Boolean(
                                timesheetApproved && timesheetStatusRollbackAllowed,
                            ) && (
                                <Can
                                    do="accept"
                                    on="timesheet"
                                >
                                    <Button
                                        className="mr-3"
                                        color="warning"
                                        onClick={() => this.showRollBackApprovedModal()}
                                    >
                                        <FontAwesomeIcon
                                            icon={faSync}
                                            className="mr-2"
                                        />
                                        Reset To Submitted
                                    </Button>
                                </Can>
                            )}

                        {(details ?? {}).timesheetStatusId
                            != TimesheetStatus.Submitted
                            && (details ?? {}).timesheetStatusId
                            != TimesheetStatus.BillingPayrollRejected
                            && this.props.payroll !== true && (
                                <Button
                                    type="button"
                                    color="secondary"
                                    className="mr-3"
                                    disabled={isPrinting}
                                    onClick={() => {
                                        if (showConfirmationPrompt) {
                                            const confirmed = window.confirm(
                                                'You have unsaved changes.  Would you like to print this timesheet without your changes?',
                                            );

                                            if (confirmed) {
                                                this.onPrint();
                                            }
                                        } else {
                                            this.onPrint();
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon
                                        size="lg"
                                        icon={
                                            isPrinting ? faCircleNotch : faPrint
                                        }
                                        className="mr-2"
                                    />
                                    Print
                                </Button>
                            )}
                        {showBillingReject && (
                            <Button
                                color="danger"
                                disabled={this.state.saving || this.state.isRejectingBilling}
                                onClick={this.onBillingRejectClicked}
                            >
                                Billing Reject
                            </Button>
                        )}
                        {(details ?? {}).timesheetStatusId == TimesheetStatus.Deleted
                            && isTimesheet === true && (
                                <Can
                                    do="delete"
                                    on="timesheet"
                                >
                                    <Button
                                        tag="span"
                                        color="warning"
                                        onClick={() => this.restoreTimesheet()}
                                        className="mr-3"
                                    >
                                        <FontAwesomeIcon
                                            icon={faUndo}
                                            className="mr-2"
                                        />
                                        Restore
                                    </Button>
                                </Can>
                            )}

                        {!lockedTimesheetStatuses.includes(details?.timesheetStatusId)
                            && isTimesheet === true
                            && details.canApproveChargeTypes && (
                                <Can
                                    do="delete"
                                    on="timesheet"
                                >
                                    <Button
                                        color="danger"
                                        onClick={() => this.getDeletionNote()}
                                        className="mr-3"
                                    >
                                        <i className="fa fa-lg fa-trash mr-2" />
                                        Delete
                                    </Button>
                                </Can>
                            )}

                        {(details ?? {}).timesheetStatusId == TimesheetStatus.ApprovalPending
                            && isTimesheet === true
                            && details.canApproveChargeTypes && (
                                <Can
                                    do="accept"
                                    on="timesheet"
                                >
                                    <Button
                                        className="mr-3"
                                        color="success"
                                        onClick={this.approveTimesheetWithValidation}
                                    >
                                        <i className="fa fa-lg fa-check mr-2" />
                                        Approve
                                    </Button>
                                </Can>
                            )}

                        {(details ?? {}).timesheetStatusId == TimesheetStatus.OperationsReview
                            && isTimesheet === true
                            && isPayroll == false && (
                                <>
                                    {!isEquipmentOnly && (
                                        <Can
                                            do="reject"
                                            on={details?.isNonFlagging ? 'timesheet' : 'flagging_timesheet'}
                                        >
                                            <Button
                                                className="mr-3"
                                                color="danger"
                                                onClick={() => this.confirmRejectTimesheet(showConfirmationPrompt, false)}
                                            >
                                                <i className="fa fa-lg fa-times mr-2" />
                                                Reject to Flagger
                                            </Button>
                                        </Can>
                                    )}
                                    {details.canApproveChargeTypes && (
                                        <Can
                                            do="accept"
                                            on="timesheet"
                                        >
                                            <Button
                                                className="mr-3"
                                                color="success"
                                                onClick={this.approveTimesheetWithValidation}
                                            >
                                                <i className="fa fa-lg fa-check mr-2" />
                                                Override Approval
                                            </Button>
                                        </Can>
                                    )}
                                </>
                            )}
                    </FlexCenterRow>
                </SlideForm>

                {Boolean(hasLoaded) && (
                    <>
                        <OrganizationContactForm
                            ref={this.contactFormRef}
                            show={showContactsForm}
                            contact={selectedContact}
                            readOnly={contactFormIsReadOnly}
                            onClose={() => {
                                this.setState({
                                    showContactsForm: false,
                                    saving: false,
                                });
                            }}
                            onChange={(e) => {
                                const { selectedContact } = { ...this.state };
                                selectedContact[e.target.name] = e.target.value;
                                this.setState({ newContact: { ...selectedContact } });
                            }}
                            onCheckedChanged={(e) => {
                                const { selectedContact } = { ...this.state };
                                selectedContact[e.target.name] = e.target.checked;
                                this.setState({ newContact: { ...selectedContact } });
                            }}
                            onContactTypeChanged={(items) => {
                                const { selectedContact } = this.state;
                                selectedContact.contactTypes = items.map(
                                    (x) => x.value,
                                );
                                this.setState({ newContact: { ...selectedContact } });
                            }}
                            onSaveCallback={this.onOrganizationContactSaved}
                        />
                        <JotFormModal
                            isOpen={showJotFormModal}
                            toggle={this.toggleJotFormModal}
                            modalMode={JotFormModalModes.VIEW}
                            formSubmission={preJobFormSubmission}
                            onSubmissionCompleted={(submission) => this.setState({ preJobFormSubmission: submission })}
                        />
                        <AddressFormNew
                            key={`ts-addr-${details?.timesheetId}`}
                            id="timesheetJobAddressForm"
                            ref={this.addressFormRef}
                            onSaveCallback={(address) => this.onSaveJobLocationAddress(address)}
                            usCounties={usCounties}
                            countryId={details?.countryId ?? this.context.tenant.countryId}
                            countryCode={details?.countryCode ?? this.context.tenant.countryCode}
                            contractId={details?.contractId}
                            isUnionWork={details?.isUnionWork}
                            contractUsesPrevailingWage={details?.contractIsPrevailingWage}
                        />
                        <AddressFormNew
                            key={`ts-meeting-addr-${details?.timesheetId}`}
                            id="timesheetMeetingAddressForm"
                            ref={this.meetingAddressFormRef}
                            onSaveCallback={(address) => this.onSaveMeetingLocationAddress(address)}
                            usCounties={usCounties}
                            countryId={details?.countryId ?? this.context.tenant.countryId}
                            countryCode={details?.countryCode ?? this.context.tenant.countryCode}
                            contractId={details?.contractId}
                            isUnionWork={details?.isUnionWork}
                            contractUsesPrevailingWage={details?.contractIsPrevailingWage}
                        />
                    </>
                )}
            </>
        );
    }
}
